// hifi-app.jsx, GrowthStack IT Complete Hi-Fi Website
// 5 pages: Home · Services · Pricing · About · Contact

// ── Paste your Apps Script Web App URL here after deploying ──
const SHEET_WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbz3VSwrX4u5tirELKvy2C4LsXG2xq4qF1HRTEPH_BL-bLpFtBamSoezU6dLGGSJSVlJ/exec';

import React from 'react';
import ReactDOM from 'react-dom/client';

const { useState, useEffect, useCallback, useRef } = React;

// ─────────────────────────────────────────────────────────────
// SHARED PRIMITIVES
// ─────────────────────────────────────────────────────────────

const LOGOMARK_SIZE = { sm: 32, lg: 44, xl: 60 };
const LogoMark = ({ size = 'sm', white = false }) => {
  const px = LOGOMARK_SIZE[size] || 32;
  // 120px source (assets/logo-mark-sm.*) rather than the full 395px assets/icon.png,
  // which is still used for the favicon and social cards. Saves ~60KB above the fold.
  return (
    <picture style={{ display: 'block', flexShrink: 0, lineHeight: 0 }}>
      <source srcSet="assets/logo-mark-sm.webp" type="image/webp" />
      <img
        src="assets/logo-mark-sm.png"
        alt="GrowthStack IT"
        width={px}
        height={px}
        style={{ display: 'block', flexShrink: 0, filter: white ? 'brightness(0) invert(1)' : 'none' }}
      />
    </picture>
  );
};

const CheckCircle = ({ muted = false }) => (
  <span className={`check-circle${muted ? ' muted' : ''}`} style={muted ? { background: '#CBD5E1' } : {}}>
    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
      <path d="M1 4l2.5 2.5L9 1" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </span>
);

const PCheck = ({ muted = false }) => (
  <span className={`p-check${muted ? ' muted' : ''}`}>
    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
      <path d="M1 3.5l2 2L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </span>
);

// ─────────────────────────────────────────────────────────────
// SITE NAV
// ─────────────────────────────────────────────────────────────

const SiteNav = ({ activePage, navigate }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const go = (p) => { setMenuOpen(false); navigate(p); };
  return (
    <nav className="site-nav">
      <div className="nav-inner">
        <div className="nav-brand" onClick={() => go('home')}>
          <LogoMark size="sm" />
          <span className="nav-wordmark">GrowthStack<span>IT</span></span>
        </div>
        <button
          className={`nav-toggle${menuOpen ? ' open' : ''}`}
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(o => !o)}
        >
          <span></span><span></span><span></span>
        </button>
        <div className={`nav-menu${menuOpen ? ' open' : ''}`}>
          <div className="nav-links">
            <button
              className={(activePage === 'services' || activePage.startsWith('service-')) ? 'active' : ''}
              onClick={() => go('services')}
            >Services</button>

            <div className="nav-drop">
              <button
                className={`nav-drop-btn${activePage === 'sectors' || activePage.startsWith('sector-') ? ' active' : ''}`}
                onClick={() => go('sectors')}
                aria-haspopup="true"
              >
                Industries
                <svg className="nav-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1l4 4 4-4" /></svg>
              </button>
              <div className="nav-drop-menu">
                {SECTORS.map(s => (
                  <button key={s.key} onClick={() => go('sector-' + s.key)}>
                    <span className="nav-drop-ico"><SectorIcon k={s.key} size={16} /></span>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            {[['pricing','Pricing'],['about','About'],['contact','Contact']].map(([p, l]) => (
              <button key={p} className={activePage === p ? 'active' : ''} onClick={() => go(p)}>{l}</button>
            ))}
          </div>
          <button className="btn btn-primary btn-sm nav-cta" onClick={() => go('contact')}>
            Book a Growth Call →
          </button>
        </div>
      </div>
    </nav>
  );
};

// ─────────────────────────────────────────────────────────────
// SITE FOOTER
// ─────────────────────────────────────────────────────────────

const SOCIALS = [
  {
    label: 'WhatsApp',
    href: 'https://wa.me/61410847664',
    icon: <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.358.101 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.585 0 11.946-5.358 11.949-11.893a11.821 11.821 0 00-3.495-8.443z"/>,
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/share/1LAZMbRUMD/',
    icon: <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/growthstackit?utm_source=qr&igsh=OWs1MmE2Y25zcGZy',
    icon: <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/growthstackit/',
    icon: <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>,
  },
];

const SiteFooter = ({ navigate }) => (
  <div className="footer-wrap">
    <footer className="footer">
      <div className="footer-top">
        <div className="footer-brand">
          <LogoMark size="lg" white />
          <span className="footer-wm">GrowthStack<span>IT</span></span>
        </div>
        <span className="footer-tagline">Managed operations · Australia-wide</span>
        <h2 className="footer-headline">Build. Grow. Automate.</h2>
        <p className="footer-sub">
          One stack. One team. One bill. Stop managing vendors and start running the business.
        </p>
        <div className="footer-cta-row">
          <input className="footer-input" type="email" placeholder="your@email.com.au" />
          <button className="footer-cta-btn" onClick={() => navigate('contact')}>Book a Growth Call →</button>
        </div>
      </div>

      <div className="footer-cols">
        <div className="footer-col">
          <h4>Contact</h4>
          <p className="footer-contact">
            Canberra, Australia<br />
            0410 847 664<br />
            sales@growthstackit.com.au<br />
            Mon–Fri 9am–5pm AEST
          </p>
          <div className="footer-socials">
            {SOCIALS.map(({ label, href, icon }) => (
              <a key={label} className="footer-soc" href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{icon}</svg>
              </a>
            ))}
          </div>
        </div>
        <div className="footer-col">
          <h4>The Stack</h4>
          <ul>
            {['IT Systems & Security','Digital Marketing','Finance & Payroll','Workflow Automation'].map(i => (
              <li key={i} onClick={() => navigate('services')}>{i}</li>
            ))}
          </ul>
        </div>
        <div className="footer-col">
          <h4>Company</h4>
          <ul>
            {[['About','about'],['Industries','sectors'],['Pricing','pricing'],['Careers','contact']].map(([l, p]) => (
              <li key={l} onClick={() => navigate(p)}>{l}</li>
            ))}
          </ul>
        </div>
        <div className="footer-col">
          <h4>Support</h4>
          <ul>
            {['Client Portal','Support Desk','Master Service Agreement','Privacy & Terms'].map(i => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="footer-legal">
        <span>© 2026 GrowthStack IT · Proudly Australian-owned</span>
        <span>Not a Registered Tax Agent or CPA firm, admin services only.</span>
      </div>
    </footer>
  </div>
);

// ─────────────────────────────────────────────────────────────
// FAQ ACCORDION
// ─────────────────────────────────────────────────────────────

const FAQ = ({ items }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="faq-list">
      {items.map(([q, a], i) => (
        <div key={i} className="faq-item">
          <button className={`faq-q${open === i ? ' open' : ''}`} onClick={() => setOpen(open === i ? null : i)}>
            {q}
            <span className={`faq-icon${open === i ? ' open' : ''}`}>+</span>
          </button>
          {open === i && <div className="faq-a">{a}</div>}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PILLAR ICONS, stroke SVGs, one per service pillar
// ─────────────────────────────────────────────────────────────

const ICONS = {
  IT: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <polyline points="9 12 11 14 15 10"/>
    </svg>
  ),
  Mk: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
      <polyline points="17 6 23 6 23 12"/>
    </svg>
  ),
  Fi: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  Op: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Tr: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 22 8 12 14 2 8 12 2"/>
      <polyline points="2 16 12 22 22 16"/>
      <polyline points="2 12 12 18 22 12"/>
    </svg>
  ),
};

// ─────────────────────────────────────────────────────────────
// HOME PAGE
// ─────────────────────────────────────────────────────────────

const PILLARS = [
  {
    icon: 'IT', label: 'Pillar A',
    title: 'IT Systems & Security',
    focus: 'Focus · Stability and Continuity',
    features: ['Google Workspace & Microsoft 365 setup', 'Cloud infrastructure management', 'Device management & MDM (Mac & PC)', 'Backup & disaster recovery'],
    outcome: 'Reliable infrastructure. Data kept in order.',
  },
  {
    icon: 'Mk', label: 'Pillar B',
    title: 'Digital Marketing & Web',
    focus: 'Focus · Lead Generation and Conversion',
    features: ['Google Ads management', 'SEO & content optimisation', 'High-converting web design', 'Landing-page A/B testing'],
    outcome: 'A predictable flow of new business.',
  },
  {
    icon: 'Fi', label: 'Pillar C',
    title: 'Finance & Payroll Admin',
    focus: 'Focus · Compliance and Cash Flow',
    features: ['Accounts payable / receivable', 'Payroll processing & STP', 'Super tracking & compliance', 'Cashflow dashboards (Xero/MYOB)'],
    outcome: 'Pro finances without a full-time CFO.',
  },
  {
    icon: 'Op', label: 'Pillar D',
    title: 'Operations & Automation',
    focus: 'Focus · Scalability',
    features: ['Workflow design & mapping', 'Zapier / Make integrations', 'GoHighLevel CRM setup', 'SOP creation & documentation'],
    outcome: 'A business that runs on systems.',
  },
];

const PILLAR_ROUTES = { IT: 'service-it', Mk: 'service-marketing', Fi: 'service-finance', Op: 'service-operations' };

const PillarGrid = ({ navigate }) => (
  <div className="pillar-grid">
    {PILLARS.map((p, i) => (
      <div key={i} className="pillar-card">
        <div className="pillar-card-head">
          <span className="pillar-icon">{ICONS[p.icon]}</span>
          <span className="pillar-label">{p.label}</span>
        </div>
        <h3>{p.title}</h3>
        <div className="pillar-focus">{p.focus}</div>
        <ul className="pillar-features">
          {p.features.map((f, j) => (
            <li key={j} className="pillar-feature">
              <CheckCircle />{f}
            </li>
          ))}
        </ul>
        <div className="pillar-outcome">
          <div className="out-label">Outcome</div>
          <div className="out-value">{p.outcome}</div>
        </div>
        {navigate && (
          <button className="pillar-detail-link" onClick={() => navigate(PILLAR_ROUTES[p.icon])}>
            Explore full details →
          </button>
        )}
      </div>
    ))}
  </div>
);

// Home-page pillar overview cards (teaser, simpler than full services cards)
const HOME_PILLARS = [
  {
    icon: 'IT', tag: 'Stability & Continuity',
    title: 'IT Systems & Security',
    items: ['Google Workspace & M365 setup', 'Cloud infrastructure & device management', 'Backup & disaster recovery'],
  },
  {
    icon: 'Mk', tag: 'Lead Generation',
    title: 'Digital Marketing & Web',
    items: ['Google & Meta Ads management', 'SEO & web optimisation', 'High-converting landing pages'],
  },
  {
    icon: 'Fi', tag: 'Compliance & Cash Flow',
    title: 'Finance & Payroll Admin',
    items: ['Payroll & STP compliance', 'Accounts payable / receivable', 'Xero & MYOB dashboards'],
  },
  {
    icon: 'Op', tag: 'Scalability',
    title: 'Operations & Automation',
    items: ['Workflow design & Zapier', 'GoHighLevel CRM setup', 'SOP creation & documentation'],
  },
];

// Tool logos for the "Built on the tools you already trust" wall.
// Monochrome brand marks (Simple Icons) stored locally in assets/logos and
// tinted via CSS mask so they read as one consistent logo strip.
const TOOL_LOGOS = [
  { slug: 'xero',           name: 'Xero' },
  { slug: 'myob',           name: 'MYOB' },
  { slug: 'google',         name: 'Google Workspace' },
  { slug: 'microsoft',      name: 'Microsoft 365' },
  { slug: 'microsoftazure', name: 'Microsoft Azure' },
  { slug: 'zapier',         name: 'Zapier' },
  { slug: 'make',           name: 'Make' },
  { slug: 'notion',         name: 'Notion' },
  { slug: 'cloudflare',     name: 'Cloudflare' },
  { slug: 'hubspot',        name: 'HubSpot' },
];

const HomePage = ({ navigate }) => (
  <>
    {/* ── Hero ── */}
    <section className="home-hero">
      <div className="container">
        <div className="hero-inner">
          <div className="hero-eyebrow">◆ Four connected services, one partner</div>
          <h1 className="hero-h1">Four pillars.<br />One connected partner.</h1>
          <p className="hero-body">
            IT Systems &amp; Security. Digital Marketing &amp; Web. Operations &amp; Automation. Finance &amp; Payroll Admin. Most growing businesses run these through four separate vendors. We run them as one accountable team.
          </p>
          <div className="hero-ctas">
            <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>Book a Growth Call →</button>
            <button className="btn btn-ghost btn-lg" onClick={() => navigate('pricing')}>See pricing</button>
          </div>
        </div>
      </div>
      <div className="hero-stats">
        {[
          ['Documented', 'accent', 'Uptime commitment in every agreement'],
          ['11 → 1',      null,     'Vendor consolidation, one Canberra client'],
          ['<15min',     'accent', 'Median P1 first response, business hours'],
          ['$2k+',       null,     'Average monthly saving on vendor spend*'],
        ].map(([n, accent, lbl], i) => (
          <div key={i} className="hero-stat">
            <div className={`n${accent ? ' accent' : ''}${n.length > 7 ? ' word' : ''}`}>{n}</div>
            <div className="lbl">{lbl}</div>
          </div>
        ))}
      </div>
      <p className="hero-stat-note">
        * Average reduction in monthly third-party vendor spend across GrowthStack IT clients, measured against their combined supplier invoices for the three months before onboarding.
      </p>
    </section>

    {/* ── Logo strip (rotating marquee) ── */}
    <div className="logo-strip">
      <div className="container">
        <div className="label">Built on the tools you already trust</div>
      </div>
      <div className="logo-marquee" aria-label="Tools we work with">
        <div className="lm-track">
          {[...Array(2)].flatMap((_, k) =>
            TOOL_LOGOS.map(l => (
              <span
                key={`${k}-${l.slug}`}
                className={k === 1 ? 'lm-logo lm-dupe' : 'lm-logo'}
                role="img"
                aria-label={l.name}
                aria-hidden={k === 1}
                title={l.name}
                style={{ maskImage: `url("assets/logos/${l.slug}.svg")`, WebkitMaskImage: `url("assets/logos/${l.slug}.svg")` }}
              />
            ))
          )}
        </div>
      </div>
    </div>

    {/* ── The Problem ── */}
    <section className="problem-section">
      <div className="container">
        <div className="problem-inner">
          <div className="problem-left">
            <div className="eyebrow">◆ The Growth Gap</div>
            <h2>Your business runs on too many tools managed by too many vendors.</h2>
            <p>
              The average Australian SME juggles 11 separate SaaS subscriptions, three or more external service providers, and an office manager who's holding it all together with spreadsheets and goodwill. Nothing is integrated. Nobody is accountable. And every month you're paying more for less.
            </p>
          </div>
          <div className="problem-stats">
            {[
              ['11', 'Average SaaS tools per AU SME, none of them integrated'],
              ['3+', 'Separate vendors billing you every month for overlapping work'],
              ['40%', 'Of owner time spent on operational admin instead of growth'],
              ['$2k+', 'Average monthly savings after consolidating to GrowthStack IT'],
            ].map(([n, lbl]) => (
              <div key={n} className="prob-stat">
                <div className="ps-n">{n}</div>
                <div className="ps-lbl">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ── Four pillars overview ── */}
    <section className="section">
      <div className="container">
        <div className="section-head">
          <div className="eyebrow">◆ The Full Stack</div>
          <h2>Four pillars. One operations team.</h2>
          <p>Each pillar is a fully managed service. Take one or take all four, they're designed to work together.</p>
        </div>
        <div className="home-pillar-grid">
          {HOME_PILLARS.map((p, i) => (
            <div key={i} className="home-pillar-card" onClick={() => navigate(PILLAR_ROUTES[p.icon])}>
              <span className="hpc-icon">{ICONS[p.icon]}</span>
              <div className="hpc-tag">{p.tag}</div>
              <h3>{p.title}</h3>
              <ul>
                {p.items.map((item, j) => (
                  <li key={j}>
                    <CheckCircle />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="hpc-link">Explore service →</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── How it works ── */}
    <section className="section section-mist">
      <div className="container">
        <div className="section-head centered">
          <div className="eyebrow">◆ How It Works</div>
          <h2>From Growth Call to operations in 30 days.</h2>
          <p>We move fast because we know your time matters more than ours.</p>
        </div>
        <div className="steps-grid">
          {[
            ['01', 'Growth Call', 'A 60-minute deep-dive into every vendor, tool, and process. We map your stack, identify every gap, and produce a report you keep.'],
            ['02', 'Consolidate', 'We migrate, standardise, and replace fragmented systems with the right tools. Same week. One dedicated pod owns every step.'],
            ['03', 'Run', 'Your pod operates the full stack day-to-day. One monthly bill, one team, one point of accountability. You get on with the business.'],
          ].map(([n, t, body]) => (
            <div key={n} className="step-card">
              <div className="step-num">{n}</div>
              <h3>{t}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>
            Book a Growth Call →
          </button>
        </div>
      </div>
    </section>

    {/* ── Why GrowthStack IT ── */}
    <section className="section">
      <div className="container">
        <div className="section-head centered">
          <div className="eyebrow">◆ How We Operate</div>
          <h2>Four principles, no exceptions.</h2>
        </div>
        <div className="values-grid">
          {[
            ['01', 'Outcomes over outputs',
              'We sell results: uptime, leads, and hours back, not retainers. Every monthly report shows the numbers, not just the activity.'],
            ['02', 'One throat to choke',
              'One pod, one bill, one accountable account lead. You never chase three vendors to find out who dropped the ball.'],
            ['03', 'Boring beats clever',
              'Reliable systems first. Cleverness only when it earns its keep. A business that works predictably is worth more than one that\'s impressive occasionally.'],
            ['04', 'AU-owned, AU-staffed',
              'Onshore data, onshore people, onshore accountability. No offshore sub-contractors without your explicit consent.'],
          ].map(([n, t, body]) => (
            <div key={n} className="value-card">
              <div className="value-num">{n}</div>
              <h4>{t}</h4>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Outcomes & Results ── */}
    <section className="section section-mist">
      <div className="container">
        <div className="section-head centered">
          <div className="eyebrow">◆ What Our Clients See</div>
          <h2>Numbers that speak for themselves.</h2>
        </div>
        <div className="outcomes-grid">
          {[
            ['Documented', 'Uptime commitment',
              'A written uptime commitment in every managed agreement, covering every device, cloud environment, and connection we run.'],
            ['3.2×',   'Average ROAS',
              'Return on ad spend across Google and Meta campaigns managed by our marketing pod.'],
            ['$2,000+','Monthly savings',
              'Average cost reduction after consolidating from multi-vendor to GrowthStack IT, before factoring in time saved.'],
            ['< 15min','Support response',
              'Median first-response time for P1 IT incidents during business hours. Same-day resolution for 92% of tickets.'],
            ['30 days','Onboarding',
              'From signed agreement to fully operational. We move fast and handle the migration so you don\'t have to.'],
            ['14-day', 'Finance close cycle',
              'From month-end to complete AP/AR reconciliation and cashflow dashboard, delivered by the 14th.'],
          ].map(([n, lbl, desc]) => (
            <div key={lbl} className="outcome-card">
              <div className={`outcome-n${n.length > 7 ? ' word' : ''}`}>{n}</div>
              <div className="outcome-label">{lbl}</div>
              <div className="outcome-desc">{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Pricing teaser ── */}
    <section className="section">
      <div className="container">
        <div className="section-head centered">
          <div className="eyebrow">◆ Pricing</div>
          <h2>Simple, monthly, no lock-in.</h2>
          <p>Three tiers built around where your business is now. Move up or down any time.</p>
        </div>
        <div className="pricing-teaser-grid">
          {[
            { tier:'TIER 1', name:'Stack Starter', price:'$1,200', per:'/ mo AUD',
              desc:'Marketing + basic IT. Ideal for early-stage SMEs getting off the ground.' },
            { tier:'TIER 2 · POPULAR', name:'Stack Growth', price:'$2,000', per:'/ mo AUD',
              desc:'IT + Marketing + Finance. The most popular choice for scaling teams.', featured: true },
            { tier:'TIER 3', name:'Stack Pro', price:'$3,000', per:'/ mo AUD',
              desc:'Full four-pillar operations system for established SMEs.' },
          ].map((t, i) => (
            <div key={i} className={`pt-card${t.featured ? ' pt-featured' : ''}`}>
              <div className="pt-tier">{t.tier}</div>
              <div className="pt-name">{t.name}</div>
              <div className="pt-price">{t.price}</div>
              <div className="pt-per">{t.per}</div>
              <div className="pt-divider"></div>
              <div className="pt-desc">{t.desc}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 36 }}>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('pricing')}>
            Compare all features →
          </button>
          <p style={{ marginTop: 14, fontSize: 13, color: 'var(--slate-400)' }}>
            Simple monthly plans · One team · One bill
          </p>
        </div>
      </div>
    </section>

    {/* ── Final CTA banner ── */}
    <section className="cta-banner">
      <div className="container">
        <div className="cta-banner-inner">
          <h2>Ready to close the growth gap?</h2>
          <p>
            Book a 60-minute Growth Call. We'll map your current stack,<br />
            identify every gap, and show you where we help most.
          </p>
          <div className="cta-banner-btns">
            <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>
              Book a Growth Call →
            </button>
            <button className="btn btn-ghost btn-lg" onClick={() => navigate('services')}>
              See what's included
            </button>
          </div>
        </div>
      </div>
    </section>

    <SiteFooter navigate={navigate} />
  </>
);

// ─────────────────────────────────────────────────────────────
// SERVICES PAGE
// ─────────────────────────────────────────────────────────────

const SERVICES_FAQ = [
  ['What does "managed operations" actually mean?',
   'We take full ownership of your IT, marketing, finance, and automation as a single function, not as separate retainers. One team is accountable for all of it, reporting to you monthly on outcomes, not hours.'],
  ['Can I choose just one or two pillars?',
   'Yes. Most clients start with IT + Marketing (Stack Starter) and add Finance or Operations as they scale. You can move between tiers with one month\'s notice.'],
  ['Do you replace our existing tools?',
   'Only where it makes sense. We standardise around a proven stack (Google Workspace or M365, Xero or MYOB, GoHighLevel, Zapier) but retain tools that are already working well.'],
  ['Where are your team members based?',
   'Australian-owned, Australian-staffed. All data is stored with AU data sovereignty by default. No offshore sub-contractors without your consent.'],
  ['What industries do you serve?',
   'We work with SMEs across professional services, trade, healthcare, hospitality, and retail, typically teams of 5–150 people with fragmented vendor stacks.'],
  ['How long does the onboarding take?',
   'Most clients are fully onboarded within 30 days. A Growth Call can be booked within 48 hours. We move fast because we know your time matters more than ours.'],
];

const ServicesPage = ({ navigate }) => (
  <>
    <div className="page-hero">
      <div className="container">
        <div className="eyebrow">◆ Services</div>
        <h1>The full operations stack.</h1>
        <p>Four pillars, delivered by one dedicated pod. Pick a package or build the whole stack.</p>
      </div>
    </div>

    {/* 2×2 pillar cards */}
    <section className="section">
      <div className="container">
        <PillarGrid navigate={navigate} />
      </div>
    </section>

    {/* Why one team changes everything */}
    <section className="section">
      <div className="container">
        <div className="diff-split">
          <div className="diff-left">
            <div className="eyebrow">◆ The Difference</div>
            <h2>Why one team changes everything.</h2>
            <p>When IT, marketing, finance, and automation share a team, they share context. Decisions become faster. Accountability becomes clear. The gaps between vendors disappear.</p>
            <ul className="diff-list">
              {[
                'No more chasing three vendors to find out who dropped the ball',
                'IT decisions that account for marketing spend and cashflow position',
                'Automations built around how your whole business actually works',
                'One monthly report covering all four functions, no assembly required',
              ].map((item, i) => (
                <li key={i}><CheckCircle />{item}</li>
              ))}
            </ul>
            <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>
              Book a Growth Call →
            </button>
          </div>
          <div className="diff-right">
            <div className="diff-before">
              <div className="diff-box-label">Before GrowthStack IT</div>
              <div className="diff-chips">
                {['IT support','Marketing agency','Bookkeeper','HR / Payroll','Web hosting','CRM tool','Automation','SaaS licensing'].map(t => (
                  <span key={t} className="diff-chip">{t}</span>
                ))}
              </div>
              <div className="diff-caption">Avg. 11 vendors · 3+ separate bills · 1 overworked office manager</div>
            </div>
            <div className="diff-arrow">↓</div>
            <div className="diff-after">
              <div className="diff-box-label after">After GrowthStack IT</div>
              <div className="diff-pod">
                <LogoMark size="lg" />
                <div>
                  <div className="diff-pod-name">Your GrowthStack IT Pod</div>
                  <div className="diff-pod-sub">1 team · 1 monthly bill · 1 outcomes report</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Feature comparison table */}
    <section className="section section-mist">
      <div className="container">
        <div className="section-head">
          <div className="eyebrow">◆ What's included</div>
          <h2>Compare by service area.</h2>
          <p>Every pillar is included in Stack Growth and Pro. Mix and match in a custom quote.</p>
        </div>
        <div className="feature-table-wrap">
          <div className="ft-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
            <span className="ft-label">Service feature</span>
            <span className="ft-col" style={{ textAlign:'center' }}>IT</span>
            <span className="ft-col" style={{ textAlign:'center' }}>Marketing</span>
            <span className="ft-col" style={{ textAlign:'center' }}>Finance</span>
            <span className="ft-col" style={{ textAlign:'center' }}>Ops</span>
          </div>
          {[
            ['Dedicated account lead',            '✓','✓','✓','✓'],
            ['Monthly outcomes report',           '✓','✓','✓','✓'],
            ['Onboarding & assessment included',  '✓','✓','✓','✓'],
            ['Google Workspace / M365 setup',     '✓','–','–','–'],
            ['Cloud infrastructure management',   '✓','–','–','–'],
            ['Device management (Mac & PC)',      '✓','–','–','–'],
            ['Backup & disaster recovery',        '✓','–','–','–'],
            ['Google / Meta Ads management',      '–','✓','–','–'],
            ['SEO & web optimisation',            '–','✓','–','–'],
            ['Payroll & super processing',        '–','–','✓','–'],
            ['Accounts payable / receivable',     '–','–','✓','–'],
            ['Cashflow dashboard (Xero/MYOB)',    '–','–','✓','–'],
            ['Workflow automation (Zapier)',      '–','–','–','✓'],
            ['GoHighLevel CRM setup',             '–','–','–','✓'],
            ['SOP creation & documentation',      '–','–','–','✓'],
          ].map((row, i) => (
            <div key={i} className="ft-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
              <span className="ft-feature">{row[0]}</span>
              {row.slice(1).map((v, j) => (
                <span key={j} className={`ft-cell ${v === '✓' ? 'ft-check' : 'ft-no'}`}>{v}</span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FAQs */}
    <section className="section">
      <div className="container">
        <div className="section-head">
          <div className="eyebrow">◆ Frequently Asked</div>
          <h2>Questions we hear every week.</h2>
        </div>
        <FAQ items={SERVICES_FAQ} />
        <div style={{ textAlign:'center', marginTop: 48 }}>
          <p style={{ color:'var(--slate-500)', marginBottom: 20 }}>
            Still have questions? Our team responds within one business hour.
          </p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>Talk to us →</button>
        </div>
      </div>
    </section>

    <SiteFooter navigate={navigate} />
  </>
);

// ─────────────────────────────────────────────────────────────
// PRICING PAGE
// ─────────────────────────────────────────────────────────────

const TIERS = [
  {
    tier: 'TIER 1', name: 'Stack Starter',
    sub: 'Marketing + basic IT for early-stage SMEs.',
    monthly: '$1,200', annual: '$1,056',
    features: [
      { t: 'Google Ads management' },
      { t: 'Google Workspace & device setup' },
      { t: 'Website maintenance' },
      { t: 'Monthly reporting' },
      { t: 'Payroll admin', muted: true },
      { t: 'Workflow automation', muted: true },
    ],
  },
  {
    tier: 'TIER 2', name: 'Stack Growth',
    sub: 'Marketing + IT + finance for scaling teams.',
    monthly: '$2,000', annual: '$1,760',
    featured: true,
    features: [
      { t: 'Everything in Starter' },
      { t: 'Payroll processing & STP' },
      { t: 'Accounts receivable' },
      { t: 'Cashflow dashboard' },
      { t: 'Quarterly strategy review' },
      { t: 'Full workflow automation', muted: true },
    ],
  },
  {
    tier: 'TIER 3', name: 'Stack Pro',
    sub: 'Full operations system for established SMEs.',
    monthly: '$3,000', annual: '$2,640',
    features: [
      { t: 'Everything in Growth' },
      { t: 'Full automation & workflow mapping' },
      { t: 'GoHighLevel CRM setup' },
      { t: 'Custom SOP library' },
      { t: 'Dedicated operations lead' },
      { t: 'Priority operations response' },
    ],
  },
];

const PRICING_FAQ = [
  ['Can I mix services, or do I have to take a bundled plan?',
   'Either. Bundled plans are the simplest way to get the full stack at one predictable price, but every service in the catalogue can be taken on its own. Most clients start with one division and expand as they see results.'],
  ['Is there a setup or onboarding fee?',
   'Yes, a one-time $950 Business Setup & Integration Fee precedes every recurring retainer relationship. It covers account provisioning, tooling, and integration. The fee is waived when it is absorbed into a larger business transformation project.'],
  ['How does billing work?',
   'Recurring monthly services are billed strictly in advance via Ignition, using automated credit-card authorisation or direct debit. If a payment remains outstanding past 72 business hours, the affected services pause until it is resolved.'],
  ['Are there minimum contract terms?',
   'Digital marketing and website maintenance retainers carry a mandatory 3-month or 6-month minimum term, which safeguards the data collection and structural optimisation those services depend on. Finance and IT retainers run month-to-month with one month\'s notice.'],
  ['Are you a CPA, BAS Agent, or registered tax agent?',
   'No. We provide finance administration and operational support only, payroll, AP/AR, cashflow, and compliance admin. We do not provide taxation, financial-product, legal, or regulated accounting advice; where required we refer you to a registered BAS Agent, Tax Agent, CPA, CA, or solicitor.'],
  ['What\'s excluded from each service?',
   'Every division lists its exclusions on this page, things like ad click spend, SaaS subscriptions, software licence seats, and cloud usage costs are billed directly to you rather than marked up. There are no hidden pass-through fees.'],
];

// Mandatory legal disclaimer, must appear on all client-facing materials.
const REG_DISCLAIMER = 'GrowthStack IT provides finance administration and operational support services. We do not provide taxation, financial product, legal, or regulated accounting advice. Where required, clients should seek advice from a registered BAS Agent, Tax Agent, CPA, CA, solicitor, or other appropriately licensed professional.';

// ── Service divisions (colour-coded catalogue categories) ──
const DIVISIONS = [
  {
    key: 'growth', num: '01', icon: 'Mk',
    name: 'Growth Marketing',
    ideal: 'For businesses that need more enquiries, stronger online visibility, and a predictable, consistent stream of inbound leads.',
    excludes: 'Direct advertising click spend, professional photography/videography production, physical print materials, premium stock photography licensing, domain name registration fees, and external web hosting packages.',
  },
  {
    key: 'systems', num: '02', icon: 'Op',
    name: 'Business Systems & AI Automation',
    ideal: 'For businesses spending too much time on manual, repetitive admin, double-handling data, and fighting disconnected systems.',
    excludes: 'Third-party SaaS subscriptions (e.g. Make, Zapier, Monday.com), API transaction/usage fees, CRM user-seat licensing, AI token/usage costs, and enterprise cloud server hosting.',
  },
  {
    key: 'finance', num: '03', icon: 'Fi',
    name: 'Finance & Payroll Administration',
    ideal: 'For growing businesses that need reliable, rock-solid payroll and finance administration without the overhead of a full-time internal finance team.',
    excludes: 'Formal BAS preparation/lodgement, corporate tax returns, regulated financial product advice, legal representation, external company auditing, third-party debt collection, and direct banking transfer/signing authority.',
  },
  {
    key: 'it', num: '04', icon: 'IT',
    name: 'IT Systems & Security',
    ideal: 'For businesses needing enterprise-grade digital stability, cloud architecture, data sovereignty, and security compliance without the overhead of an internal IT department.',
    excludes: 'Direct hardware procurement, financing or leasing; on-site cabling, server racking or office network runs; full cyber-incident forensics or legal representation during data breaches; and high-frequency general end-user phone support for basic office-app training.',
  },
  {
    key: 'transform', num: '05', icon: 'Tr',
    name: 'Business Transformation Projects',
    ideal: 'For businesses ready to move off scattered spreadsheets and inboxes onto one properly built operating system, in a single staged engagement.',
    excludes: 'Ongoing retainer support after handover, third-party platform subscriptions and user-seat licensing, connector and API usage fees, and any advisory work reserved for a registered BAS Agent, Tax Agent, CPA, CA, or solicitor.',
    note: 'The three tiers are cumulative: Growth includes everything in Foundation, and Enterprise includes everything in Growth.',
  },
];

// ── À la carte service catalogue. Recurring monthly retainers get the
//    annual (−12%) discount; project, quarterly, and per-unit prices are
//    shown as-is. Every entry carries the full includes / limits / needs
//    scope from the current edition of the service catalogue. ──
const CATALOGUE = [
  // Division 1, Growth Marketing
  { div: 'growth', name: 'Google Ads Management', price: 1250, unit: '/ month', type: 'recurring',
    desc: 'A fully managed Google Ads presence built around your real service area and offer, with weekly optimisation that keeps cost per lead trending down instead of drifting up.',
    includes: [
      'Full Search and Performance Max campaign architecture, structured around your service lines rather than one catch-all campaign',
      'Keyword intent research mapped into ad groups that mirror how your customers actually search',
      'A live, continuously refined negative-keyword list that filters out irrelevant clicks',
      'Target-CPA bid strategy setup, monitored and tuned monthly as data accumulates',
      'Ad copy and asset rotation inside the Google testing framework',
      'Weekly Search Terms Report reviews to block wasted spend before it compounds',
      'A monthly ROI report covering cost per lead, cost per conversion, and trend versus prior month',
      'A quarterly strategy session on competitor movement, keyword expansion, and landing page recommendations',
    ],
    limits: [
      'Ad spend is billed directly to your own card inside Google Ads; we never hold or touch your media budget',
      'Covers up to 3 live campaigns at a time; a 4th campaign is scoped and quoted separately',
      'Landing page design and build not included, see Website Creation below',
      'Deep multi-step CRM lead-routing integrations sit outside this service, see Workflow Automation',
    ],
    needs: [
      'An active Google Ads MCC link established before your first campaign goes live',
      'Your payment card loaded into Google Billing with a spend cap set at your comfort level',
      'GA4 and Google Tag Manager installed and verified firing before launch',
    ] },
  { div: 'growth', name: 'Social Media Management', price: 1500, unit: '/ month', type: 'recurring',
    desc: 'A complete monthly content engine: strategy, design, copywriting, scheduling, and reporting handled end to end, with daily engagement monitoring so conversations do not sit unanswered.',
    includes: [
      'A monthly content calendar built around approved themes, seasonal moments, and your offering',
      '12 fully designed, on-brand posts per month, each with custom copy written in your brand voice',
      'Scheduling and publishing across your connected platforms',
      'Daily engagement monitoring, 15 minutes per business day, flagging comments, messages, and mentions',
      'A monthly metrics report covering reach, engagement rate, and top-performing post',
    ],
    limits: [
      'Original photography and video production not included; content is built from supplied assets, stock imagery, and design',
      'Engagement monitoring is monitor-and-flag, not full community management',
      'Paid social boosting or amplification spend not included',
      'Platforms beyond those confirmed at kickoff require a scope adjustment',
    ],
    needs: [
      'Monthly content themes approved before the month begins',
      'Admin-level (not editor-only) access to every platform being managed',
      'A current brand asset folder: logo files, colours, fonts, existing photography',
    ] },
  { div: 'growth', name: 'Ad Content Creation', price: 750, unit: '/ month', type: 'recurring',
    desc: 'Dedicated ad copywriting and creative direction keeping your paid campaigns supplied with fresh, tested variations built for performance, not repurposed organic content.',
    includes: [
      'Up to 8 ad copy variations per month, written specifically for A/B testing',
      'Headline, primary text, and description variations built around your live offer',
      'Creative direction briefs for up to 4 paired visual or video concepts per month',
      'Copy checked against brand voice and current compliance requirements before it goes live',
      'Every variant logged so you can see what has been tried and what performed',
    ],
    limits: [
      'Filming, video production, and stock footage licensing not included; copy and creative direction only',
      'Available only as an add-on to an active Google Ads or Social Media Management retainer',
      'Running the campaigns themselves is billed separately under Google Ads Management',
    ],
    needs: [
      'A signed offer/messaging brief confirming the current promotion, pricing, or angle',
      'Shared access to the relevant ad account so approved copy can be loaded directly',
    ] },
  { div: 'growth', name: 'Graphic Designer Retainer', price: 1000, unit: '/ month', type: 'recurring',
    desc: 'An on-call design resource for the everyday visual assets a growing business needs, produced against your brand guidelines without the overhead of an in-house hire.',
    includes: [
      'Up to 10 unique visual assets per month: social tiles, display ad creative, flyers, brochures, and deck slides',
      'Every asset produced to your brand colour palette, typography, and visual style',
      'Up to 2 rounds of revisions per asset included',
      'Final files delivered in client-usable formats, ready to publish or print',
      'Requests triaged and prioritised each month against your real deadlines',
    ],
    limits: [
      'Commercial print production costs not included; this covers design only',
      'Stock image and stock video licensing excluded and billed at cost',
      'A 3rd revision round on any single asset is billed at $75 per asset',
    ],
    needs: [
      'A brand style guide or equivalent reference materials on file before the first request',
      'Monthly asset requests submitted by an agreed cut-off date',
    ] },

  { div: 'growth', sub: 'Website creation · one-off projects', name: 'Landing Page (1-Page Funnel)', price: 1300, unit: 'one-off project', type: 'project',
    desc: 'A single, focused conversion page built around one offer and one call to action, the fastest way to get a campaign or lead-gen push live without a full website build.',
    includes: [
      'A single-page conversion funnel, designed and built end to end around one clear offer',
      'Fully responsive layout, tested across mobile, tablet, and desktop',
      'Structural copywriting laid out to support your single call to action',
      'Lead capture form integration, connected and tested before launch',
      'GA4 and Google Tag Manager tracking installed and verified',
    ],
    limits: [
      'Exactly one page; no navigation menu or linked sub-pages',
      'Exactly 2 rounds of consolidated revisions; a 3rd round is billed at $220 per hour',
      'Domain registration and hosting subscriptions not included and paid directly by you',
      'Raw copy supplied by you, or purchased separately as Landing Page Copy at $380',
    ],
    needs: [
      'An approved Website Architecture Brief on file before build begins',
      'Copy, logo files, and media assets uploaded to the shared drive before the build starts',
    ] },
  { div: 'growth', sub: 'Website creation · one-off projects', name: 'Basic Website (up to 5 pages)', price: 2000, unit: 'one-off project', type: 'project',
    desc: 'A complete small-business website across Home, About, Services, Contact, and Gallery, built to represent your business properly online without the scope of a larger site.',
    includes: [
      'Strategic page layout mapped to a standard small business structure',
      'Fully responsive build, tested across desktop, tablet, and mobile',
      'Standard lead capture form configuration on your contact page',
      'Basic on-page technical SEO: meta titles, descriptions, and heading structure on every page',
      'Google Search Console connected and linked from day one',
    ],
    limits: [
      'Upfront creative copywriting not included; raw content supplied by you at onboarding',
      'Complex multi-step forms, domain procurement, and premium plugin licences excluded',
      'Capped at 5 pages; a 6th page moves the project into the Intermediate tier',
      'Exactly 2 rounds of consolidated revisions; a 3rd round is billed at $220 per hour',
    ],
    needs: [
      'An approved Website Architecture Brief on file before build begins',
      'Copy, logo files, and media assets uploaded to the shared drive',
    ] },
  { div: 'growth', sub: 'Website creation · one-off projects', name: 'Intermediate Website (6 to 9 pages)', price: 2400, unit: 'one-off project', type: 'project', featured: true, bundle: 'Most popular',
    desc: 'Everything the Basic tier offers, extended with service-specific content, a blog or case-study framework, and smarter contact handling, for businesses ready to say more.',
    includes: [
      'Everything in the Basic Website tier, built out across 6 to 9 pages',
      'Service-specific page breakouts so each offering gets its own dedicated page',
      'A basic blog framework or case-study architecture, ready for ongoing content',
      'Interactive FAQ accordions to handle common questions',
      'Customised contact routing logic so enquiries reach the right inbox automatically',
    ],
    limits: [
      'Advanced e-commerce checkout functionality not included',
      'Member portal logins and custom database-backed features excluded',
      'Capped at 9 pages; a 10th page moves the project into the Business tier',
      'Exactly 2 rounds of consolidated revisions; a 3rd round is billed at $220 per hour',
    ],
    needs: [
      'An approved Website Architecture Brief on file before build begins',
      'Copy, logo files, and media assets uploaded to the shared drive',
    ] },
  { div: 'growth', sub: 'Website creation · one-off projects', name: 'Business Website (10+ pages)', price: 2800, unit: 'one-off project', type: 'project',
    desc: 'An enterprise-depth website for businesses with multiple locations, larger teams, or a broader story to tell, including hands-on training so your team can manage it after launch.',
    includes: [
      'Enterprise-level, multi-page strategic flow mapping across 10 or more pages',
      'A dedicated portfolio or project index in a structured, browsable format',
      'Extensive team profile arrays giving your people a proper presence',
      'Advanced multi-location page layouts for businesses across more than one site',
      '1 hour of platform administration training for your team',
    ],
    limits: [
      'Custom programmatic app integrations (booking, reservations) require a separate quote',
      'Copywriting, domain procurement, and premium plugin licences remain excluded',
      'Exactly 2 rounds of consolidated revisions; a 3rd round is billed at $220 per hour',
    ],
    needs: [
      'An approved Website Architecture Brief on file before build begins',
      'Copy, logo files, team photos, and media assets uploaded to the shared drive',
    ] },

  { div: 'growth', sub: 'Website maintenance · quarterly retainers', name: 'Website Maintenance (Basic)', price: 550, unit: '/ quarter', type: 'recurring', noAnnual: true, badge: 'Quarterly retainer',
    desc: 'Ongoing quarterly care that keeps your website secure, backed up, and current, without you needing to think about it.',
    includes: [
      'Plugin and core software security updates applied every quarter',
      'A monthly offsite backup sweep, so a recent restorable copy always exists',
      'Ongoing platform security monitoring between visits',
      'Broken hyperlink checks and mitigation across the site',
      'Up to 2 hours per month of client-requested text or image changes',
    ],
    limits: [
      'Major layout redesigns and structural changes not included',
      'Custom application programming and emergency code rollbacks excluded',
      'Unused monthly hours do not roll over into the next month or quarter',
    ],
    needs: [
      'Admin-level access to the web hosting panel and platform dashboard',
      'A signed Remote Access Consent Form on file before work begins',
    ] },
  { div: 'growth', sub: 'Website maintenance · quarterly retainers', name: 'Website Maintenance (Technical)', price: 750, unit: '/ quarter', type: 'recurring', noAnnual: true, badge: 'Quarterly retainer',
    desc: 'Everything in Basic Maintenance, stepped up for sites on a more technical hosting or plugin stack that need closer security attention.',
    includes: [
      'Everything included in Website Maintenance (Basic)',
      'Priority security patching, applied ahead of the standard maintenance queue',
      'Deeper malware and file-integrity scanning to catch issues before they become incidents',
    ],
    limits: [
      'Major layout redesigns, custom programming, and emergency rollbacks remain excluded',
      'Unused monthly hours do not roll over',
    ],
    needs: [
      'Admin-level access to the web hosting panel and platform dashboard',
      'A signed Remote Access Consent Form on file before work begins',
    ] },

  // Division 2, Business Systems & AI Automation
  { div: 'systems', name: 'Workflow Mapping (Business Process Blueprint)', price: 1500, unit: 'fixed project', type: 'project',
    desc: 'A structured look at how your business actually runs today and a clear blueprint for how it should run tomorrow, the foundation most automation work should start from.',
    includes: [
      'A current-state process map for up to 10 named workflows, built from real discovery conversations',
      'A future-state redesign for each workflow, focused on removing friction and manual steps',
      'A documentation manual capturing every mapped process in a reusable format',
      'An automation-opportunity list flagging where each workflow could realistically be automated',
      'A full implementation roadmap sequencing what to tackle first',
    ],
    limits: [
      'Blueprint only; no build, automation, or system configuration work included',
      'Capped at 10 workflows; an 11th workflow is scoped and quoted separately',
    ],
    needs: [
      'A signed engagement brief locking in the workflow count and names before scheduling',
      'Confirmed availability of relevant staff for discovery interviews',
    ] },
  { div: 'systems', name: 'CRM Setup', price: 1625, unit: 'fixed project', type: 'project',
    desc: 'A properly configured CRM instance with pipeline, roles, templates, and training, so your team starts using it correctly from week one.',
    includes: [
      'Full CRM instance deployment and base configuration',
      'A custom sales pipeline built to match how your team actually sells',
      'User roles and permissions configured for up to 10 team members',
      'Standard communication templates built and loaded, ready to use',
      '2 hours of live administrative training for your nominated CRM admin',
      'Import of client-supplied contact data in standard format',
    ],
    limits: [
      'Hard-capped at 10 active users; an 11th user requires a new quote',
      'Complex legacy data migrations and extensive custom API programming excluded',
      'The CRM platform subscription cost sits with you',
    ],
    needs: [
      'An active CRM platform subscription in place before setup begins',
      'Confirmed pipeline stages and full team user list, provided in writing',
    ] },
  { div: 'systems', name: 'Workflow Automation', price: 2250, unit: 'up to 5 flows', type: 'project',
    desc: 'Five real automated workflows, not templates, built around how leads, notifications, and calendar events actually need to move through your business.',
    includes: [
      '5 automated multi-step workflows, built to your signed brief',
      'Automated lead capture and distribution alerts so new leads reach the right person immediately',
      'Calendar integrations, syncing bookings or events automatically',
      'Cross-platform notifications keeping your team informed without manual checking',
    ],
    limits: [
      'Capped at 5 flows; a 6th flow is scoped and quoted as a new project',
      'Third-party connector licensing fees (Zapier, Make, or similar) not included',
      'Custom webhook and bespoke API development sit outside this service',
    ],
    needs: [
      'Trigger and action logic for all 5 flows signed off in writing before build starts',
      'Both source and destination platforms live, accessible, and licensed',
    ] },
  { div: 'systems', name: 'Full Business Automation', price: 4500, plus: true, unit: 'custom scope', type: 'project',
    desc: 'A comprehensive, custom-scoped automation build spanning multiple departments, for businesses ready to move beyond a handful of flows into a genuinely connected operation.',
    includes: [
      'Automation architecture spanning marketing, finance, and operations, scoped to your approved brief',
      'Custom API and webhook development wherever standard connectors fall short',
      'Cross-system integration linking CRM, finance tools, and operational systems',
      'Layered error-handling and escalation logic so failures are flagged, not silently lost',
      '4 hours of live administrative training for your team',
    ],
    limits: [
      'Priced from; the final figure is confirmed only after discovery',
      'A completed Workflow Mapping engagement is strongly recommended first, though not compulsory',
      'Ongoing third-party connector and licensing fees not included',
    ],
    needs: [
      'A nominated technical point of contact with admin access to every relevant system',
      'A signed-off blueprint, existing or run concurrently, confirming full scope',
    ] },

  // Division 3, Finance & Payroll Administration
  { div: 'finance', sub: 'Finance Operations · recurring retainers', name: 'Payroll Administration', price: 250, unit: '/ user / month', type: 'recurring', noAnnual: true,
    desc: 'Weekly payroll handled properly and on time, with a hard rule that nothing gets paid without your explicit sign-off first.',
    includes: [
      'Weekly pay cycle processing for each employee on the service',
      'Employee leave tracking, kept current every pay run',
      'Payslip generation and distribution',
      'Single Touch Payroll (STP) alignment, submitted following your authorisation',
      'Processing aligned to current Payday Super same-day compliance requirements',
    ],
    limits: [
      'Priced per employee on a weekly pay cycle; fortnightly or monthly cycles quoted separately',
      'Award interpretation, employment contract drafting, and HR legal advice not included',
      'Payroll software licensing (e.g. Xero Premium) sits with you',
    ],
    needs: [
      'A signed Payroll Change Request for any new hire or pay variance before it is actioned',
      'Your bank account linked to the Payday Super clearing house before the first pay run',
    ] },
  { div: 'finance', sub: 'Finance Operations · recurring retainers', name: 'Accounts Payable (AP)', price: 625, unit: '/ month', type: 'recurring',
    desc: 'Supplier invoices captured, coded, and reconciled every month, with payment authority staying 100% in your hands at all times.',
    includes: [
      'Supplier invoice data capture and ledger processing for up to 120 invoices per month',
      'Automated payment scheduling built around your approval thresholds',
      'Digital sorting and cloud archiving of every source document',
      'Overdue-payment tracking reminders at 7, 14, and 30 days',
      'Routine bank-feed reconciliation, matching payouts to open bills',
    ],
    limits: [
      'Hard-capped at 120 invoices per month; invoice 121 onward triggers an overage quote',
      'Direct supplier payment authorisation remains 100% with you; we never move your money',
      'Supplier price negotiations and payment disputes not included',
    ],
    needs: [
      'A dedicated accounts@ inbox forwarding into your Xero or MYOB account before month one',
      'Approval thresholds documented in writing for management-level sign-off',
    ] },
  { div: 'finance', sub: 'Finance Operations · recurring retainers', name: 'Accounts Receivable (AR)', price: 625, unit: '/ month', type: 'recurring',
    desc: 'Customer invoices out the door on time, followed up automatically, and reconciled against what actually lands in your account.',
    includes: [
      'Customer invoice generation for up to 120 invoices per month',
      'Monthly account statement production',
      'Automated follow-up communications at 7, 14, and 30 days overdue',
      'Payment tracking and reconciliation against incoming funds',
    ],
    limits: [
      'Hard-capped at 120 invoices per month; invoice 121 onward triggers an overage quote',
      'Physical debt collection, court filing, and asset recovery not included',
    ],
    needs: [
      'A confirmed invoicing address or inbox in place before month one',
      'Payment terms and accepted payment methods agreed in writing',
    ] },
  { div: 'finance', sub: 'Finance Operations · recurring retainers', name: 'Cashflow Management', price: 750, unit: '/ month', type: 'recurring',
    desc: 'A clear monthly picture of where your cash is heading: forecast, dashboard, and variance report, so surprises get caught early instead of at tax time.',
    includes: [
      'Monthly structural cashflow forecasting charts built against your historical trend',
      'A custom operational KPI dashboard, kept current every month',
      'Strategic budget variance monitoring, flagging where actuals diverge from plan',
    ],
    limits: [
      'Monthly cadence only; not available as an ad-hoc mid-cycle service',
      'Regulated investment advice, asset deployment strategy, and tax mitigation consulting not included',
    ],
    needs: [
      'Bank feeds connected and reconciled before the service begins',
      'A minimum of 3 months of transaction history to build the initial trend line',
    ] },
  { div: 'finance', sub: 'Finance Operations · recurring retainers', name: 'Compliance Administration', price: 437.5, unit: '/ month', type: 'recurring',
    desc: 'A running compliance calendar and reminder system, so deadlines for BAS, super, insurance, and licences never sneak up on you.',
    includes: [
      'Automated compliance calendar tracking every deadline relevant to your business',
      'Reminder alerts sent at 14 days and 3 days out from each deadline',
      'Operational document organisation, keeping supporting paperwork filed and findable',
      'Internal reminder tracking protocols confirming the completion status of each item',
    ],
    limits: [
      'Lodgement of BAS, corporate income tax returns, and ASIC filings not included',
      'Formal regulatory financial audits not included; this is a tracking and organisation service',
    ],
    needs: [
      'A confirmed deadline list agreed with you or your accountant, in writing',
    ] },
  { div: 'finance', sub: 'Finance Systems · fixed projects', name: 'Finance Systems Setup', price: 1500, unit: 'fixed project', type: 'project',
    desc: 'A properly configured finance stack from day one: chart of accounts, bank feeds, templates, and tax mapping, so your books start clean instead of needing a clean-up later.',
    includes: [
      'Chart of accounts configured to match your business structure',
      'Bank feeds connected and tested',
      'Invoice and quote templates built to your brand',
      'GST/tax code mapping completed and verified',
      'User roles and permissions configured within the accounting platform',
      'Opening balance entry using only client-approved figures',
    ],
    limits: [
      'Historical data migration beyond the current financial year not included',
      'Complex multi-entity consolidations and any lodgement or advisory work excluded',
    ],
    needs: [
      'An active accounting platform subscription (Xero or MYOB) before setup begins',
      'Opening balances supplied and approved by you or your accountant',
    ] },
  { div: 'finance', sub: 'Finance Systems · fixed projects', name: 'Finance Automation Build', price: 1750, unit: 'up to 5 flows', type: 'project',
    desc: 'Five finance-specific automations layered on top of a working finance system: reminders, reconciliation rules, and alerts that run themselves.',
    includes: [
      'Up to 5 automated finance workflows, chosen from overdue invoice reminders, bank feed reconciliation rules, cashflow alert triggers, payroll processing triggers, and compliance deadline notifications',
    ],
    limits: [
      'Requires a completed Finance Systems Setup before this build can start',
      'Connector licensing fees and custom API development excluded',
    ],
    needs: [
      'Finance systems setup live and confirmed working before build starts',
      'Approval thresholds for any automated actions defined in writing',
    ] },

  // Division 4, IT Systems & Security
  { div: 'it', name: 'Cloud Workspace Migration', price: 2250, unit: 'up to 10 users', type: 'project',
    desc: 'A clean, monitored move to Google Workspace or Microsoft 365. Mail, calendar, and files migrated and verified, with 48 hours of close monitoring after cutover.',
    includes: [
      'Full migration of email, calendar, and files for up to 10 users',
      'DNS/MX record cutover timed to an agreed window',
      'New user account provisioning on the destination platform',
      'A 30-minute team walkthrough session',
      '48 hours of active mail-flow monitoring immediately after cutover',
    ],
    limits: [
      'Capped at 10 users; an 11th user is billed at approximately $360 each',
      'Legacy PST or mailbox archive migrations not included',
      'Custom app and API integrations and third-party licensing costs excluded',
    ],
    needs: [
      'Admin access to both source and destination platforms, confirmed before scheduling',
      'A cutover window locked in writing in advance',
    ] },
  { div: 'it', name: 'Identity, Domain & DNS Configuration', price: 625, unit: 'fixed project', type: 'project',
    desc: 'The email and identity foundations of your domain set up correctly, SPF, DKIM, DMARC, and baseline SSO, so deliverability and security are not left to chance.',
    includes: [
      'MX, SPF, DKIM, and DMARC records configured correctly',
      'Baseline identity/SSO policy set up within your chosen platform',
      'Full documentation of every DNS change made, filed for future reference',
    ],
    limits: [
      'Domain registration and renewal fees excluded and billed directly to you',
      'Complex multi-domain or multi-tenant identity federation not included',
    ],
    needs: [
      'Registrar-level access, or a contact who can action DNS changes on request',
      'Your target mail/identity platform confirmed in writing',
    ] },
  { div: 'it', name: 'Technical Onboarding & Offboarding Kits', price: 250, unit: 'per employee event', type: 'project', badge: 'Per event',
    desc: 'A clean, repeatable process for bringing staff on and off your systems, so every account, licence, and access point is handled the same way every time.',
    includes: [
      'New-starter onboarding: account creation, core software licences, and access permissions from day one',
      'Or departing-staff offboarding: access revoked, device wiped or returned, and licences reallocated',
      'Priced per kit, per individual employee event',
    ],
    limits: [
      'Physical device procurement not included and billed separately',
      'Events involving 5 or more staff at once require a custom quote',
    ],
    needs: [
      'A signed Onboarding/Offboarding Request Form on file before any action is taken',
    ] },
  { div: 'it', name: 'Azure & Cloud Server Management', price: 1250, unit: '/ month · up to 3 servers', type: 'recurring',
    desc: 'Hands-on management of your cloud servers: monitored, patched, backed up, and reported on every month, so infrastructure stays a non-issue.',
    includes: [
      'Proactive monitoring of up to 3 cloud servers or virtual machines',
      'Monthly security patch deployment during an agreed maintenance window',
      'Backup job verification, confirming the backup completed successfully on every server',
      'A monthly performance and uptime report',
    ],
    limits: [
      'Capped at 3 servers/VMs; a 4th server is billed at approximately $300 each',
      'Application-level troubleshooting and custom infrastructure builds not included',
      'Disaster recovery execution beyond backup verification excluded',
    ],
    needs: [
      'Administrative access to your cloud environment, confirmed before month one',
      'A documented server inventory on file',
    ] },
  { div: 'it', name: 'End-User Fleet Management', price: 31.25, unit: '/ device / month · 5 device min', type: 'recurring', noAnnual: true,
    desc: 'Day-to-day monitoring and light-touch support across every device your team uses, so small issues get caught before they become downtime.',
    includes: [
      'RMM-based monitoring across every device in your fleet',
      'Asset inventory tracking, kept current as devices are added or retired',
      'Basic remote troubleshooting support when something is flagged',
    ],
    limits: [
      'A 5-device minimum applies',
      'On-site hardware repair and hardware procurement not included',
    ],
    needs: [
      'Monitoring agent installed on every in-scope device',
      'A signed device inventory list on file',
    ] },
  { div: 'it', name: 'Software Licensing & Patch Management', price: 21.88, unit: '/ device / month · 5 device min', type: 'recurring', noAnnual: true,
    desc: 'Every device kept current on patches and every software licence tracked, so nothing lapses quietly and nothing goes unpatched.',
    includes: [
      'OS and core application patch deployment across your fleet',
      'Software licence tracking with renewal alerts sent ahead of expiry',
    ],
    limits: [
      'A 5-device minimum applies',
      'Licence costs themselves excluded and billed directly to you',
    ],
    needs: [
      'Patch management agent installed on every in-scope device',
      'A current licence inventory supplied by you',
    ] },
  { div: 'it', name: 'Mobile Device Management (iOS & Android)', price: 25, unit: '/ device / month · 5 device min', type: 'recurring', noAnnual: true,
    desc: 'Enrolled, policy-enforced mobile devices with remote wipe capability, so a lost or stolen phone does not become a data breach.',
    includes: [
      'Full MDM enrolment for every in-scope device',
      'Policy enforcement covering passcode requirements and encryption',
      'Remote lock and wipe capability, ready to use if a device is lost or stolen',
      'Basic app management across enrolled devices',
    ],
    limits: [
      'A 5-device minimum applies',
      'Personally-owned (BYOD) devices require a signed BYOD policy acknowledgment before enrolment',
    ],
    needs: [
      'Signed MDM enrolment consent per device or user',
      'A security policy baseline agreed before rollout',
    ] },

  // Division 5, Business Transformation Projects (cumulative tiers)
  { div: 'transform', name: 'Foundation Project', price: 3500, unit: 'one-off project', type: 'project',
    desc: 'The starting engagement for businesses moving off scattered spreadsheets and inboxes: a proper discovery process, mapped workflows, and a native CRM set up right from the beginning.',
    includes: [
      'A business discovery session covering objectives, stakeholders, and current-state snapshot',
      'Deep workflow mapping across up to 10 core workflows, current-state and future-state',
      'A native CRM architecture deployed and configured to your business',
      'A sales/ops pipeline built to the discovery brief',
      'User permission profiles configured by role',
      'Core notification routing: lead alerts and task-assignment notifications',
      'Baseline documentation: a process manual plus a CRM admin guide',
    ],
    limits: [
      'Workflow automation, reporting dashboards, and system migrations not included at this tier',
      'CRM capped at 10 users and workflow mapping at 10 workflows',
      'One discovery session included; additional sessions are billed at $150 each',
      'Notification routing covers core lead/task alerts only',
    ],
    needs: [
      'A signed Foundation Project scope brief',
      'Confirmed stakeholder availability for the discovery session before scheduling',
      'An active CRM platform subscription before setup starts',
    ] },
  { div: 'transform', name: 'Growth Project', price: 8750, unit: 'one-off project', type: 'project', featured: true, bundle: 'Includes Foundation',
    desc: 'Everything in Foundation, extended with real automation, a live reporting dashboard, and a properly organised shared drive, for businesses ready to run on systems rather than manual effort.',
    includes: [
      'Everything included in the Foundation Project',
      'Multi-channel workflow automation, up to 8 automated flows across CRM, email, and connected systems',
      'An automated reporting dashboard, live-linked to your CRM and connected systems',
      'Centralised Google Workspace shared drive migration: folder structure, permissions, and files',
      'Team training across the CRM, the new automations, and the shared drive structure',
    ],
    limits: [
      'Multi-department rollout, custom AI/document processing, and ongoing consultation sit outside this tier',
      'Automation capped at 8 flows; a 9th flow is scoped and quoted separately',
      'Shared drive migration covers files and folders only, not full mailbox migration',
      'Training capped at one structured session per system, up to 4 hours total',
    ],
    needs: [
      'Foundation Project scope completed, or run concurrently as one combined engagement',
      'Admin access to your Google Workspace environment confirmed',
      'Target automation platforms and connectors confirmed before build starts',
    ] },
];

// ── Rules that apply across a whole sub-category, printed under its grid ──
const SUB_RULES = {
  'Website creation · one-off projects': {
    title: 'Website service rules, applying to every tier:',
    items: [
      'The Rule of Two: exactly 2 rounds of consolidated revisions are included with every website build. A 3rd round is billed at $220 per hour.',
      'Domain registration, hosting subscriptions, and premium plugin licences are excluded from every tier and paid direct by the client.',
      'Raw copy is supplied by the client at onboarding, or purchased separately as Landing Page Copy at $380 or a custom copywriting package.',
    ],
  },
};

// ── Hardened client engagement policy ──
const ENGAGEMENT_POLICY = [
  {
    title: 'Mandatory setup fee',
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    body: <>Every recurring retainer begins with a one-time <strong>$950 Business Setup &amp; Integration Fee</strong>, waived only when it is absorbed into a larger business transformation project.</>,
  },
  {
    title: 'Billing & payment',
    icon: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></>,
    body: <>Recurring services are billed strictly <strong>in advance via Ignition</strong>, using automated card authorisation or direct debit. Services pause if a payment is outstanding past <strong>72 business hours</strong>.</>,
  },
  {
    title: 'Cancellation & handover',
    icon: <><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
    body: <>Core finance and IT retainers run <strong>month-to-month</strong> with one month's written notice. On exit we hand over all work, credentials, and documentation in full.</>,
  },
  {
    title: 'Commitment windows',
    icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    body: <>Digital marketing and website maintenance retainers carry a <strong>3-month or 6-month minimum term</strong> to safeguard data collection and initial structural optimisation.</>,
  },
];

// Whole dollars render clean; per-device and per-month rates that carry cents
// (e.g. $437.50, $21.88) keep exactly two decimals.
const money = (n) => '$' + n.toLocaleString('en-US',
  Number.isInteger(n) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Annual discount, rounded to whole dollars on whole-dollar rates and to
// cents on rates that already carry them.
const annualRate = (n) =>
  Number.isInteger(n) ? Math.round(n * 0.88) : Math.round(n * 88) / 100;

// Mandatory setup-fee band, reused in both plans & catalogue views.
const SetupFeeCard = () => (
  <div className="setup-fee-card">
    <div>
      <div className="sf-eyebrow">Getting started</div>
      <h3>$950 Business Setup &amp; Integration Fee</h3>
      <p>Every recurring retainer relationship begins with a one-time setup &amp; integration fee, covering account provisioning, tooling, and onboarding. It's waived when absorbed into a larger business transformation project.</p>
    </div>
    <div className="setup-fee-amount">
      <div className="amt">$950</div>
      <div className="lbl">one-time · per engagement</div>
    </div>
  </div>
);

const modeFromHash = () =>
  (typeof window !== 'undefined' && window.location.hash === '#plans') ? 'plans' : 'catalogue';

const PricingPage = ({ navigate }) => {
  const [mode, setModeState] = useState(modeFromHash);   // 'catalogue' (default) | 'plans'
  const [annual, setAnnual] = useState(false);
  const [divFilter, setDivFilter] = useState('all');
  const [openDetail, setOpenDetail] = useState({});

  // Reflect the active tab in the URL hash so a copied link reopens the same view.
  const setMode = (m) => {
    setModeState(m);
    if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
      const base = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', base + '#' + m);
    }
  };

  // Keep the view in sync if the hash changes (manual edit, back/forward).
  useEffect(() => {
    const onHash = () => setModeState(modeFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const priceView = (s) => {
    if (s.type === 'recurring' && !s.noAnnual && typeof s.price === 'number') {
      return annual
        ? { amount: money(annualRate(s.price)), strike: money(s.price) }
        : { amount: money(s.price) };
    }
    return { amount: s.display || money(s.price), plus: s.plus };
  };

  const ServiceCard = (s) => {
    const pv = priceView(s);
    const hasDetail = s.includes || s.limits || s.needs;
    const open = !!openDetail[s.name];
    return (
      <div key={s.name} className={`svc-card${s.featured ? ' featured' : ''}`}>
        {s.bundle && <span className="svc-bundle-badge">{s.bundle}</span>}
        <div className="svc-name">{s.name}</div>
        <div className="svc-desc">{s.desc}</div>
        <div className="svc-price-row">
          {pv.strike && <span className="svc-strike">{pv.strike}</span>}
          <span className="svc-amount">{pv.amount}{pv.plus ? '+' : ''}</span>
          <span className="svc-unit">{s.unit}</span>
        </div>
        <div className="svc-badge-row">
          <span className={`svc-badge ${s.type}`}>
            {s.badge || (s.type === 'recurring' ? 'Monthly retainer' : 'Fixed project')}
          </span>
        </div>
        {annual && s.type === 'recurring' && !s.noAnnual && (
          <div className="svc-annual-note">Billed annually · save 12%</div>
        )}
        {hasDetail && (
          <button
            className={`svc-detail-toggle${open ? ' open' : ''}`}
            aria-expanded={open}
            onClick={() => setOpenDetail(o => ({ ...o, [s.name]: !o[s.name] }))}
          >
            {open ? 'Hide details' : "What's included"} <span className="caret">▾</span>
          </button>
        )}
        {hasDetail && open && (
          <div className="svc-detail">
            {s.includes && (
              <>
                <h5 className="inc">Includes</h5>
                <ul>{s.includes.map((x, i) => <li key={i} className="inc">{x}</li>)}</ul>
              </>
            )}
            {s.limits && (
              <>
                <h5 className="lim">Limitations</h5>
                <ul>{s.limits.map((x, i) => <li key={i} className="lim">{x}</li>)}</ul>
              </>
            )}
            {s.needs && (
              <>
                <h5 className="req">What we need from you</h5>
                <ul>{s.needs.map((x, i) => <li key={i} className="req">{x}</li>)}</ul>
              </>
            )}
          </div>
        )}
        <button className="btn btn-secondary btn-full svc-cta" onClick={() => navigate('contact')}>Enquire →</button>
      </div>
    );
  };

  const renderDivision = (d) => {
    const svcs = CATALOGUE.filter(s => s.div === d.key);
    const noSub = svcs.filter(s => !s.sub);
    const subs = [...new Set(svcs.map(s => s.sub).filter(Boolean))];
    return (
      <div key={d.key} className="svc-div-group">
        <div className="svc-div-head">
          <div className="svc-div-badge">{ICONS[d.icon]}</div>
          <div className="svc-div-titles">
            <div className="svc-div-eyebrow">Division {d.num}</div>
            <div className="svc-div-name">{d.name}</div>
            <div className="svc-div-ideal">{d.ideal}</div>
          </div>
        </div>
        {noSub.length > 0 && <div className="svc-grid">{noSub.map(ServiceCard)}</div>}
        {subs.map(sub => (
          <React.Fragment key={sub}>
            <div className="svc-subcat"><span>{sub}</span></div>
            <div className="svc-grid">{svcs.filter(s => s.sub === sub).map(ServiceCard)}</div>
            {SUB_RULES[sub] && (
              <div className="sub-rules">
                <strong>{SUB_RULES[sub].title}</strong>
                <ul>{SUB_RULES[sub].items.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
          </React.Fragment>
        ))}
        {d.note && <div className="div-note">{d.note}</div>}
        <div className="div-exclusions"><strong>Not included:</strong> {d.excludes}</div>
      </div>
    );
  };

  const shownDivs = divFilter === 'all' ? DIVISIONS : DIVISIONS.filter(d => d.key === divFilter);

  return (
    <>
      <div className="page-hero">
        <div className="container">
          <div className="eyebrow">◆ Plans &amp; Pricing</div>
          <h1>Plans &amp; pricing.</h1>
          <p>Take the whole stack as a bundled plan, or build your own from the service catalogue. Transparent AUD pricing, no marked-up pass-through costs.</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {/* Primary segmented control */}
          <div className="plans-tabs-wrap">
            <div className="plans-tabs">
              <button className={`plans-tab${mode === 'catalogue' ? ' active' : ''}`} onClick={() => setMode('catalogue')}>Service Catalogue</button>
              <button className={`plans-tab${mode === 'plans' ? ' active' : ''}`} onClick={() => setMode('plans')}>Bundled Plans</button>
            </div>
          </div>

          {/* Promo banner */}
          <div className="pricing-promo-wrap">
            <div className="pricing-promo">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l1.9 5.8L20 9.7l-4.9 3.6L17 19l-5-3.6L7 19l1.9-5.7L4 9.7l6.1-1.9L12 2z" />
              </svg>
              One team, one monthly bill, across all four divisions.
            </div>
          </div>

          {/* Division filter (catalogue only) */}
          {mode === 'catalogue' && (
            <div className="div-filter">
              <button
                className={`div-pill${divFilter === 'all' ? ' active' : ''}`}
                onClick={() => setDivFilter('all')}
              >
                <span className="div-pill-ico">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
                </span>
                All services
              </button>
              {DIVISIONS.map(d => (
                <button
                  key={d.key}
                  className={`div-pill${divFilter === d.key ? ' active' : ''}`}
                  onClick={() => setDivFilter(d.key)}
                >
                  <span className="div-pill-ico">{ICONS[d.icon]}</span>
                  {d.name}
                </button>
              ))}
            </div>
          )}

          {/* Billing toggle */}
          <div className="billing-toggle-wrap" style={{ marginTop: 34 }}>
            <div className="billing-toggle">
              <button className={`tog-btn${!annual ? ' active' : ''}`} onClick={() => setAnnual(false)}>Pay monthly</button>
              <button className={`tog-btn${annual ? ' active' : ''}`} onClick={() => setAnnual(true)}>
                Pay annually&nbsp;<span className="save-note">· save 12%</span>
              </button>
            </div>
          </div>

          {/* ── Bundled plans view ── */}
          {mode === 'plans' && (
            <>
              <div className="pricing-grid">
                {TIERS.map((t, i) => (
                  <div key={i} className={`pricing-card${t.featured ? ' featured' : ''}`}>
                    {t.featured && <span className="popular-badge">Most Popular</span>}
                    <div className="pricing-tier">{t.tier}</div>
                    <div className="pricing-name">{t.name}</div>
                    <div className="pricing-sub">{t.sub}</div>
                    <div className="pricing-price-row">
                      <span className="pricing-amount">{annual ? t.annual : t.monthly}</span>
                      <span className="pricing-per">/ month AUD</span>
                    </div>
                    {annual && (
                      <div className="pricing-annual-note">Billed annually · save 12%</div>
                    )}
                    <div className="pricing-divider"></div>
                    <div className="pricing-features">
                      {t.features.map((f, j) => (
                        <div key={j} className="pricing-feature">
                          <PCheck muted={f.muted} />{f.t}
                        </div>
                      ))}
                    </div>
                    <button
                      className={`btn btn-full${t.featured ? ' btn-primary' : ' btn-dark'}`}
                      style={!t.featured ? { background:'rgba(255,255,255,.1)', borderColor:'rgba(255,255,255,.2)', color:'rgba(255,255,255,.85)' } : {}}
                      onClick={() => navigate('contact')}
                    >
                      Start with {t.name.split(' ')[1]}
                    </button>
                  </div>
                ))}
              </div>

              <SetupFeeCard />

              <div className="custom-band">
                <div>
                  <div className="eyebrow" style={{ marginBottom: 8 }}>◆ Stack Custom</div>
                  <h3>Need something else? We'll build the stack to fit.</h3>
                  <p>For multi-entity, enterprise, or compliance-heavy operations.</p>
                </div>
                <button className="btn btn-dark btn-lg" onClick={() => navigate('contact')}>Talk to sales →</button>
              </div>

              <div className="trust-chips">
                <span className="chip chip-teal">AU-owned &amp; staffed</span>
                <span className="chip chip-teal">No hidden pass-through costs</span>
                <span className="chip chip-teal">Cancel core retainers any time</span>
                <span className="chip chip-teal">Secure billing via Ignition</span>
              </div>
            </>
          )}

          {/* ── Service catalogue view ── */}
          {mode === 'catalogue' && (
            <div className="svc-catalogue">
              {shownDivs.map(renderDivision)}
              <SetupFeeCard />
            </div>
          )}
        </div>
      </section>

      {/* Feature comparison, bundled plans only */}
      {mode === 'plans' && (
        <section className="section section-mist">
          <div className="container">
            <div className="section-head">
              <div className="eyebrow">◆ Compare</div>
              <h2>What's included in each plan.</h2>
            </div>
            <div className="feature-table-wrap">
              <div className="ft-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
                <span className="ft-label">Feature</span>
                <span className="ft-col" style={{ textAlign:'center' }}>Starter</span>
                <span style={{ textAlign:'center' }}>
                  <span className="ft-col-featured">Growth</span>
                </span>
                <span className="ft-col" style={{ textAlign:'center' }}>Pro</span>
              </div>
              {[
                ['Google Ads management',            '✓','✓','✓'],
                ['Google Workspace & device setup',  '✓','✓','✓'],
                ['Website maintenance',              '✓','✓','✓'],
                ['Monthly outcomes report',          '✓','✓','✓'],
                ['Payroll admin',                    '–','✓','✓'],
                ['Accounts receivable',              '–','✓','✓'],
                ['Cashflow dashboard',               '–','✓','✓'],
                ['Quarterly strategy review',        '–','✓','✓'],
                ['Workflow automation',              '–','–','✓'],
                ['GoHighLevel CRM setup',            '–','–','✓'],
                ['Custom SOP library',               '–','–','✓'],
                ['Dedicated operations lead',        '–','–','✓'],
                ['Response time (business hours)', '24 hrs','4 hrs','<15 min'],
              ].map((row, i) => (
                <div key={i} className="ft-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
                  <span className="ft-feature">{row[0]}</span>
                  {row.slice(1).map((v, j) => (
                    <span key={j} className={`ft-cell ${v === '✓' ? 'ft-check' : v === '–' ? 'ft-no' : j === 1 ? 'ft-text-f' : 'ft-text'}`}>{v}</span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Engagement policy + regulatory disclaimer */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Engagement Policy</div>
            <h2>How we work together.</h2>
            <p>Clear terms up front, so there are no surprises once we're running your operations.</p>
          </div>
          <div className="policy-grid">
            {ENGAGEMENT_POLICY.map((p, i) => (
              <div key={i} className="policy-card">
                <div className="pc-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{p.icon}</svg>
                </div>
                <h4>{p.title}</h4>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
          <div className="reg-disclaimer">
            <svg className="rd-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="8" /><line x1="12" y1="12" x2="12" y2="16" />
            </svg>
            <p><strong>Regulatory notice.</strong> {REG_DISCLAIMER}</p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ FAQ</div>
            <h2>Pricing questions, answered.</h2>
          </div>
          <FAQ items={PRICING_FAQ} />
        </div>
      </section>

      <SiteFooter navigate={navigate} />
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// SECTORS PAGE
// ─────────────────────────────────────────────────────────────

const SECTOR_ICONS = {
  tradies: <><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.7 2.7-2-2 2.7-2.7z" /></>,
  construction: <><path d="M3 18a9 9 0 0 1 18 0" /><path d="M9 8V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" /><line x1="2" y1="18" x2="22" y2="18" /></>,
  healthcare: <><path d="M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.5-7 10-7 10z" /><path d="M9 11h6M12 8v6" /></>,
  professional: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
  hospitality: <><path d="M4 8h13a3 3 0 0 1 0 6h-1" /><path d="M4 8v7a4 4 0 0 0 4 4h5a4 4 0 0 0 4-4V8z" /><line x1="8" y1="2" x2="8" y2="4" /><line x1="12" y1="2" x2="12" y2="4" /></>,
  retail: <><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></>,
  realestate: <><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" /><path d="M9 21v-6h6v6" /></>,
  fitness: <><rect x="1.5" y="9" width="3" height="6" rx="1" /><rect x="19.5" y="9" width="3" height="6" rx="1" /><rect x="5" y="7" width="3" height="10" rx="1" /><rect x="16" y="7" width="3" height="10" rx="1" /><line x1="8" y1="12" x2="16" y2="12" /></>,
};

// ─────────────────────────────────────────────────────────────
// SHARED SECTOR CONTENT
// ─────────────────────────────────────────────────────────────

// One primary CTA label across the whole site.
const CTA_LABEL = 'Book a Growth Call →';

// The four connected capabilities. Short labels for cards; full names in the model section.
const CAPABILITY_LABELS = ['IT', 'Marketing', 'Automation', 'Finance Admin'];

const CONNECTED_MODEL = [
  ['IT Systems & Security', 'Build a dependable technology foundation for your people, devices, accounts and business information.'],
  ['Marketing & Acquisition', 'Create visibility and generate suitable enquiries through websites, campaigns and content.'],
  ['Automation & Business Systems', 'Move enquiries and work through the business with fewer manual steps.'],
  ['Finance Administration', 'Improve the administration surrounding payroll, supplier payments, invoicing and cash flow.'],
];

const COMMON_PROBLEMS = [
  'Unreliable systems interrupt daily work',
  'Staff access and business information are poorly controlled',
  'Enquiries are not followed up consistently',
  'Customer and job information is scattered',
  'Repetitive administration consumes valuable time',
  'Invoices and outstanding payments are managed manually',
];

// A secure IT foundation sits underneath every stage of this journey.
const BUSINESS_JOURNEY = ['Enquiry', 'Qualification', 'Quote or Proposal', 'Delivery', 'Invoice', 'Payment', 'Reporting', 'Repeat Business'];

const SUITABILITY = {
  good: [
    'Australian trades, service businesses and SMEs',
    'Businesses with growing teams and systems',
    'Companies losing time through repetitive administration',
    'Businesses with inconsistent enquiry follow-up',
    'Teams that need better control over accounts, access and information',
    'Owners who want their technology, marketing and administration connected',
  ],
  bad: [
    'Businesses seeking only consumer computer repairs',
    'Organisations expecting guaranteed sales or revenue',
    'Companies needing regulated accounting, legal or financial advice',
    'Businesses unwilling to provide the access and cooperation required',
    'Enterprises requiring large-scale, 24/7 infrastructure beyond our capacity',
  ],
};

// Required disclaimer. Applies wherever Finance Administration is offered.
const FINANCE_DISCLAIMER = 'Finance Administration is limited to operational and administrative support. Taxation, legal advice, statutory audit, regulated accounting and financial advice are not included unless separately delivered by an appropriately qualified professional.';

// Pillar metadata for the sector service-stack cards. Equal weight; IT is not dominant.
const STACK_PILLARS = [
  { key: 'it', label: 'IT Systems & Security', icon: 'IT' },
  { key: 'marketing', label: 'Marketing & Acquisition', icon: 'Mk' },
  { key: 'automation', label: 'Automation & Business Systems', icon: 'Op' },
  { key: 'finance', label: 'Finance Administration', icon: 'Fi' },
];

// Engagement-level inclusions shared by every sector (no fabricated numbers).
const BASE_INCLUDED = [
  'Setup, configuration and ongoing management of the services above',
  'Staff onboarding and offboarding as your team changes',
  'A single accountable GrowthStack team and one monthly bill',
  'Monthly reporting on activity and outcomes',
];

const SECTORS = [
  {
    key: 'tradies', name: 'Tradies', tag: 'Trades & field services', priority: true,
    h1: 'More booked jobs. Less chasing. Reliable systems in the field.',
    heroDesc: 'We help electricians, plumbers, tilers, landscapers, cleaners and other field-service businesses improve their IT systems, generate local enquiries and reduce the administration between quoting, completing work and getting paid.',
    problem: 'Missed calls and slow quotes turn into lost jobs.',
    cardOutcome: 'More booked jobs, from first contact to payment.',
    whoFor: ['Electricians', 'Plumbers', 'HVAC businesses', 'Tilers & waterproofers', 'Landscapers', 'Painters', 'Cleaners', 'Maintenance contractors'],
    challenges: [
      'Quotes and invoices chased by hand',
      'Missed calls turning into lost jobs',
      'Paper timesheets and manual pay runs',
      'No system for follow-ups or reviews',
      'Job details scattered across texts and notebooks',
    ],
    stack: {
      it: ['Microsoft 365 or Google Workspace for mobile staff', 'Device and access management', 'Cloud file access', 'Staff onboarding and offboarding', 'Backup and essential security controls'],
      marketing: ['Google Ads', 'Local landing pages', 'Website management', 'Social media content', 'Campaign tracking'],
      automation: ['CRM and lead pipeline', 'Missed-enquiry responses', 'Quote follow-up', 'Job notifications', 'Review-request automation'],
      finance: ['Invoice administration', 'Payment reminders', 'Accounts payable and receivable', 'Payroll administration', 'Cash-flow monitoring'],
    },
    workflow: ['Google enquiry', 'Automatic acknowledgement', 'CRM record', 'Quote follow-up', 'Job confirmed', 'Invoice issued', 'Payment reminder', 'Review request'],
    outcomes: ['Enquiries answered and acknowledged automatically', 'Quotes followed up without manual chasing', 'Invoices issued and reminders sent for you', 'Reliable systems your mobile team can rely on'],
    included: ['Local lead generation and a fast, quote-focused website', ...BASE_INCLUDED],
    notIncluded: ['Guaranteed job numbers or lead volumes', 'Guaranteed IT uptime or 24/7 support unless separately agreed', 'Support for every trade platform', 'Tax or accounting advice'],
    related: ['construction', 'realestate', 'professional'],
    faqs: [
      ['Do you work with small trade businesses?', 'Yes. Most of our trade clients are teams of 2 to 20. We scale the stack to your size and only add what earns its keep.'],
      ['Can you get us more local jobs?', 'That is the point of the marketing side: local Google Ads, a fast quote-focused website, and a Google Business Profile tuned for your service area. We do not guarantee job numbers.'],
      ['Will this work while we are on site all day?', 'Everything runs from your phone. Quotes, follow-ups, and approvals are automated so the office work happens without you at a desk.'],
    ],
    seoTitle: 'Marketing, IT & Admin for Tradies | GrowthStack IT',
    seoDesc: 'Managed IT, marketing, automation and finance admin for Australian trade businesses. More booked jobs, automated quotes and follow-ups, less paperwork. Book a Growth Call.',
  },
  {
    key: 'construction', name: 'Construction', tag: 'Building & construction', priority: true,
    h1: 'Keep projects, systems and cash flow moving.',
    heroDesc: 'We help construction businesses improve system access, enquiry handling, document workflows, supplier administration and payment visibility.',
    problem: 'Progress claims, documents and approvals slow the whole job down.',
    cardOutcome: 'Cleaner project admin and clearer cash-flow visibility.',
    whoFor: ['Residential builders', 'Commercial construction firms', 'Fit-out & renovation companies', 'Civil & infrastructure contractors', 'Subcontractor groups', 'Project & site managers'],
    challenges: [
      'Progress claims and retentions tracked in spreadsheets',
      'Subcontractor payments and compliance to manage',
      'Document versions scattered across the team',
      'Slow, painful month-end',
      'Onboarding site staff and devices from scratch each time',
    ],
    stack: {
      it: ['Office and site-user setup', 'Secure cloud file access', 'Account permissions', 'Device configuration', 'Backup and security controls'],
      marketing: ['Website and project portfolio', 'Lead-generation campaigns', 'Tender and enquiry landing pages', 'Capability-statement content', 'Campaign reporting'],
      automation: ['Enquiry and tender pipeline', 'Approval workflows', 'Supplier-document routing', 'Internal notifications', 'Progress-claim reminders'],
      finance: ['Supplier invoice administration', 'Accounts payable and receivable', 'Progress-payment follow-up', 'Payroll administration', 'Cash-flow reporting support'],
    },
    workflow: ['Project enquiry', 'Qualification', 'Estimate or tender', 'Acceptance', 'Project administration', 'Progress claim', 'Payment follow-up', 'Project close'],
    outcomes: ['Tenders and enquiries tracked in one pipeline', 'Approvals and documents moving with fewer manual steps', 'Progress claims and supplier payments kept on schedule', 'Clearer cash-flow visibility across projects'],
    included: ['Project, tender and supplier administration workflows', ...BASE_INCLUDED],
    notIncluded: ['Quantity surveying, certification or engineering advice', 'Legal contract administration', 'Guaranteed project profitability', 'Specialist site infrastructure we do not genuinely support'],
    related: ['tradies', 'realestate', 'professional'],
    faqs: [
      ['Can you handle progress claims and retentions?', 'Yes. We set up accounts receivable and payable around your claim schedule and track retentions so nothing is missed.'],
      ['Do you support multiple projects at once?', 'Cashflow and reporting are structured per project, so you can see where each job stands at any time.'],
      ['Can you manage devices for site teams?', 'Yes. We enrol and secure phones, tablets, and laptops so site staff have what they need from day one.'],
    ],
    seoTitle: 'Finance, Systems & IT for Construction Firms | GrowthStack IT',
    seoDesc: 'Managed IT, automation, marketing and finance admin for Australian construction and building companies. Progress claims, cashflow, and compliance handled. Book a Growth Call.',
  },
  {
    key: 'professional', name: 'Professional Services', tag: 'Agencies, consultants & firms', priority: true,
    h1: 'Secure systems. A stronger pipeline. Smoother client delivery.',
    heroDesc: 'We help professional firms protect business information, attract suitable clients and reduce the manual work between enquiry, onboarding, delivery and payment.',
    problem: 'Billable hours disappear into manual admin between enquiry and payment.',
    cardOutcome: 'More billable time and a steadier client pipeline.',
    whoFor: ['Agencies & studios', 'Consultancies', 'Accounting & bookkeeping firms', 'Law & advisory practices', 'Engineering & architecture firms', 'IT & technical services'],
    challenges: [
      'Billable hours lost to manual admin',
      'Disconnected CRM, invoicing, and email',
      'Inconsistent, unpredictable lead generation',
      'Manual reporting and reconciliations',
      'Scaling without piling on overhead',
    ],
    stack: {
      it: ['Business email and user accounts', 'Secure file sharing', 'MFA and access controls', 'Remote-work configuration', 'Staff onboarding and offboarding'],
      marketing: ['Website positioning', 'Google or LinkedIn campaigns where appropriate', 'Thought-leadership content', 'Landing pages', 'Performance reporting'],
      automation: ['CRM setup', 'Proposal workflows', 'Client onboarding', 'Meeting follow-up', 'Renewal reminders'],
      finance: ['Invoice administration', 'Payment follow-up', 'Accounts payable and receivable', 'Payroll administration', 'Cash-flow monitoring'],
    },
    workflow: ['Website enquiry', 'Discovery booking', 'CRM opportunity', 'Proposal', 'Acceptance', 'Onboarding', 'Delivery', 'Invoice', 'Renewal'],
    outcomes: ['More of the day spent on billable work', 'A steadier, better-qualified client pipeline', 'Onboarding and proposals handled consistently', 'Business information kept secure and controlled'],
    included: ['CRM, proposal and client-onboarding workflows', ...BASE_INCLUDED],
    notIncluded: ['Guaranteed contracts or client wins', 'Legal or financial advice', 'Replacing specialist practice systems', 'Generic automation applied without reviewing your process'],
    related: ['realestate', 'healthcare', 'construction'],
    faqs: [
      ['Can you connect our CRM, invoicing, and email?', 'Yes. We integrate your tools and automate the handoffs so data stops being re-entered by hand.'],
      ['Do you help with lead generation?', 'The marketing side builds a pipeline through SEO, content, and paid channels, measured against a number. We do not guarantee contracts.'],
      ['How do you protect our knowledge if someone leaves?', 'We document workflows as SOPs so the business does not depend on any one person, including us.'],
    ],
    seoTitle: 'Automation, Marketing & Finance for Professional Services | GrowthStack IT',
    seoDesc: 'Managed automation, IT, marketing and finance admin for Australian agencies, consultants, and firms. Reclaim billable hours and build a predictable pipeline. Book a Growth Call.',
  },
  {
    key: 'healthcare', name: 'Healthcare', tag: 'Clinics & allied health',
    blurb: 'Secure business systems and simpler non-clinical administration for clinics and allied health.',
    h1: 'Reliable business systems and simpler non-clinical administration.',
    heroDesc: 'We support clinics and allied health providers with secure business technology, approved marketing, enquiry workflows and non-clinical finance administration.',
    problem: 'Non-clinical admin and disconnected systems pull time away from patients.',
    cardOutcome: 'Reliable systems and lighter non-clinical admin.',
    whoFor: ['GP & medical clinics', 'Allied health practices', 'Physiotherapy & chiropractic', 'Dental practices', 'Psychology & counselling', 'Specialist & multi-site clinics'],
    challenges: [
      'Patient and practice data across disconnected tools',
      'Manual billing and slow receivables',
      'Record-keeping and compliance overhead',
      'Onboarding practitioners takes too long',
      'Keeping business data secure and stored appropriately',
    ],
    stack: {
      it: ['Staff accounts and permissions', 'MFA and device controls', 'Secure business email', 'Approved cloud-system configuration', 'Backup and continuity planning'],
      marketing: ['Website management', 'Local visibility', 'Approved advertising support', 'Educational content', 'Enquiry tracking'],
      automation: ['Non-clinical enquiry workflows', 'Intake-form routing', 'Administrative reminders', 'Referral administration', 'Management reporting'],
      finance: ['Accounts payable and receivable', 'Payroll administration', 'Invoice follow-up', 'Cash-flow administration'],
    },
    workflow: ['Enquiry', 'Non-clinical intake routing', 'Administrative reminder', 'Referral administration', 'Invoice', 'Payment follow-up', 'Management reporting'],
    outcomes: ['Non-clinical enquiries routed and followed up', 'Reliable, secure business systems for your team', 'Invoices and payments administered for you', 'Faster, standardised staff onboarding'],
    included: ['Secure business systems and non-clinical enquiry workflows', ...BASE_INCLUDED],
    notIncluded: ['Diagnosis, treatment or clinical automation', 'Patient outcome or compliance guarantees', 'Unapproved health claims', 'Clinical data in unsuitable systems, or access without a documented privacy and security review'],
    disclaimer: 'GrowthStack IT provides technology and business administration support. Clinical decisions, patient records, privacy obligations and professional compliance remain the responsibility of the healthcare provider.',
    related: ['professional', 'fitness', 'retail'],
    faqs: [
      ['Is our business data kept secure?', 'Yes. Access controls, MFA, backup and continuity planning are built in, configured on systems approved for your practice.'],
      ['Can you help onboard new practitioners quickly?', 'Standardised onboarding provisions access, email, and devices in a repeatable way so new team members are ready fast.'],
      ['Do you provide clinical or medical software?', 'No. We manage the surrounding business systems, IT, and finance admin. Clinical software stays with your specialist vendor and we integrate around it.'],
    ],
    seoTitle: 'Managed IT, Systems & Finance for Healthcare | GrowthStack IT',
    seoDesc: 'Secure business IT, approved marketing, and non-clinical finance admin for Australian clinics and allied health. Reliable systems, lighter admin. Book a Growth Call.',
  },
  {
    key: 'hospitality', name: 'Hospitality', tag: 'Cafes, restaurants & venues',
    blurb: 'Fill quiet periods and keep payroll, suppliers and bookings under control.',
    h1: 'Keep customers coming in and the back office running.',
    heroDesc: 'We help hospitality businesses fill quiet periods, respond to booking and function enquiries, and keep payroll, suppliers and cash flow under control behind the scenes.',
    problem: 'Thin margins, busy rosters and enquiries scattered everywhere.',
    cardOutcome: 'Busier tables and a back office that keeps up.',
    whoFor: ['Cafes & restaurants', 'Bars & pubs', 'Function & event venues', 'Caterers', 'Accommodation providers', 'Multi-site hospitality groups'],
    challenges: [
      'Rostering and payroll across casual staff',
      'Slow, manual supplier payments',
      'Quiet mid-week periods to fill',
      'Reviews and bookings scattered everywhere',
      'High staff turnover and onboarding',
    ],
    stack: {
      it: ['Business email and staff accounts', 'Device configuration', 'Staff and guest Wi-Fi planning', 'Access controls', 'File storage and backups'],
      marketing: ['Local campaigns', 'Social media management', 'Promotional creative', 'Website and booking-page care', 'Event promotions'],
      automation: ['Booking-enquiry responses', 'Function-enquiry workflows', 'Customer follow-up', 'Promotion scheduling', 'Internal notifications'],
      finance: ['Supplier accounts payable', 'Payroll administration', 'Invoice administration', 'Cash-flow monitoring'],
    },
    workflow: ['Booking or function enquiry', 'Automatic response', 'Customer follow-up', 'Promotion scheduling', 'Supplier payment', 'Payroll run', 'Cash-flow reporting'],
    outcomes: ['Booking and function enquiries answered promptly', 'Quiet periods targeted with local campaigns', 'Payroll and supplier payments handled', 'A back office that keeps up with the floor'],
    included: ['Local marketing plus booking and function enquiry workflows', ...BASE_INCLUDED],
    notIncluded: ['Food safety or liquor-licensing advice', 'Guaranteed bookings', 'Support for every POS platform', 'Payment-card compliance guarantees', 'Customer marketing without proper consent'],
    related: ['retail', 'fitness', 'tradies'],
    faqs: [
      ['Can you handle payroll for casual staff?', 'Yes. We run pay for casual and shift-based teams with STP-ready processing.'],
      ['Can you help fill quiet weeknights?', 'Local social and paid campaigns, plus booking follow-ups, are aimed squarely at your slower periods. We do not guarantee bookings.'],
      ['Do you integrate with our POS?', 'We work around your existing point of sale and booking tools, connecting them where it saves time rather than replacing them.'],
    ],
    seoTitle: 'Payroll, Marketing & Systems for Hospitality | GrowthStack IT',
    seoDesc: 'Managed payroll, IT, marketing and systems for Australian cafes, restaurants, and venues. Fill quiet periods, keep the back office running. Book a Growth Call.',
  },
  {
    key: 'retail', name: 'Retail & E-commerce', tag: 'Retail & online stores',
    blurb: 'Connect your technology, customer acquisition and store administration.',
    h1: 'Connect your technology, customer acquisition and store administration.',
    heroDesc: 'We help retail and e-commerce businesses connect their technology, drive customer acquisition, and streamline the administration behind orders, suppliers and cash flow.',
    problem: 'Standing out online while order and supplier admin is manual.',
    cardOutcome: 'More visibility and less manual store admin.',
    whoFor: ['Independent retailers', 'Online & e-commerce stores', 'Multi-channel sellers', 'Showrooms & boutiques', 'Wholesale & supply', 'Franchise operators'],
    challenges: [
      'Standing out online against bigger players',
      'Manual stock and order admin',
      'Reconciling payments across channels',
      'Inconsistent marketing',
      'Slow, disconnected systems',
    ],
    stack: {
      it: ['Staff accounts and permissions', 'Device configuration', 'Business email', 'Cloud storage', 'Security controls and backup'],
      marketing: ['Paid advertising', 'Campaign creative', 'Website and landing-page improvements', 'Promotional content', 'Performance reporting'],
      automation: ['Customer follow-up', 'CRM and email workflows', 'Abandoned-enquiry workflows', 'Internal notifications', 'Reporting dashboards'],
      finance: ['Supplier accounts payable', 'Accounts receivable where applicable', 'Payroll administration', 'Cash-flow reporting support'],
    },
    workflow: ['Customer enquiry or order', 'Automatic follow-up', 'CRM and email workflow', 'Abandoned-enquiry recovery', 'Supplier payment', 'Cash-flow reporting'],
    outcomes: ['More qualified traffic to your store or site', 'Customer follow-up running automatically', 'Order and supplier admin handled once, not twice', 'Clearer reporting across channels'],
    included: ['Paid acquisition plus customer and order-admin workflows', ...BASE_INCLUDED],
    notIncluded: ['Guaranteed ROAS or sales', 'Inventory or fulfilment management unless separately agreed', 'Marketplace management unless supported', 'Payment-card compliance guarantees', 'Integration with every e-commerce platform'],
    related: ['hospitality', 'fitness', 'professional'],
    faqs: [
      ['Can you drive more traffic to our store?', 'Yes. Paid search and social plus SEO are built to bring qualified buyers to your site or shopfront. We do not guarantee ROAS.'],
      ['Do you build or manage our online store?', 'We build fast, conversion-focused websites and connect the tools around them. Large custom platforms are scoped as a project.'],
      ['Can you automate order and stock admin?', 'We map and automate the repetitive steps between your store, invoicing, and finance so orders stop being handled twice.'],
    ],
    seoTitle: 'Marketing, Web & Automation for Retail & E-commerce | GrowthStack IT',
    seoDesc: 'Managed marketing, web, IT and automation for Australian retail and online stores. More visibility, less manual store admin. Book a Growth Call.',
  },
  {
    key: 'realestate', name: 'Real Estate', tag: 'Agencies & property', priority: true,
    h1: 'Faster enquiry response. Safer information. Smoother property administration.',
    heroDesc: 'We help real estate and property teams respond to enquiries faster, keep business information secure, and simplify the recurring property, supplier and client administration behind every listing and managed property.',
    problem: 'Enquiries go cold and property admin piles up between listings.',
    cardOutcome: 'Faster enquiry response and simpler recurring admin.',
    whoFor: ['Sales agencies', 'Property management teams', 'Strata & building managers', 'Buyers agents', 'Commercial property services', 'Real estate admin teams'],
    challenges: [
      'Leads going cold from slow follow-up',
      'Listings and marketing done by hand',
      'Recurring property and supplier admin to manage',
      'Contacts and pipelines scattered',
      'Onboarding new agents each time',
    ],
    stack: {
      it: ['Agent and staff account setup', 'Secure file sharing', 'Mobile-device access', 'Permissions and onboarding', 'Backup and security controls'],
      marketing: ['Listing and service campaigns', 'Website management', 'Lead-generation landing pages', 'Social media content', 'Campaign reporting'],
      automation: ['Enquiry assignment', 'Appointment reminders', 'Vendor and buyer follow-up', 'Maintenance-request routing', 'CRM workflows'],
      finance: ['Supplier invoice administration', 'Accounts payable and receivable', 'Payment follow-up', 'Payroll administration', 'Cash-flow support'],
    },
    workflow: ['Enquiry', 'Assigned to agent', 'Appointment reminder', 'Vendor and buyer follow-up', 'CRM record', 'Supplier administration', 'Payment follow-up', 'Reporting'],
    outcomes: ['Enquiries assigned and followed up faster', 'Business information kept secure across devices', 'Recurring property and supplier admin simplified', 'Payments and reporting handled behind the scenes'],
    included: ['Listing marketing plus enquiry and property-admin workflows', ...BASE_INCLUDED],
    notIncluded: ['Property valuations', 'Trust accounting unless separately authorised and qualified', 'Tenancy or legal advice', 'Guaranteed listings or sales', 'Automation that replaces licensed-agent responsibilities'],
    disclaimer: 'GrowthStack IT provides technology and business administration support only. Property valuations, trust accounting, tenancy and legal matters remain the responsibility of appropriately licensed and qualified professionals.',
    related: ['professional', 'construction', 'tradies'],
    faqs: [
      ['Can you automate lead follow-up?', 'Yes. Every enquiry is captured and followed up automatically so leads stop going cold.'],
      ['Do you market individual listings?', 'We run listing campaigns across search and social and keep your brand visible in your service area. We do not guarantee listings or sales.'],
      ['Can you help onboard new agents?', 'Onboarding provisions access, email, and devices so new agents are productive quickly.'],
    ],
    seoTitle: 'CRM, Marketing & Systems for Real Estate | GrowthStack IT',
    seoDesc: 'Managed CRM, IT, marketing and finance admin for Australian real estate and property teams. Faster enquiry response, simpler recurring admin. Book a Growth Call.',
  },
  {
    key: 'fitness', name: 'Fitness & Wellness', tag: 'Gyms, studios & wellness',
    blurb: 'Turn more trial enquiries into members without losing control of the admin.',
    h1: 'Turn more enquiries into members without losing control of the admin.',
    heroDesc: 'We help gyms, studios and wellness businesses convert more trial enquiries into members and keep billing, payroll and systems running in the background.',
    problem: 'Trial enquiries slip and membership admin eats the day.',
    cardOutcome: 'More members and reliable billing.',
    whoFor: ['Gyms & fitness centres', 'Boutique studios', 'Personal training businesses', 'Yoga & pilates studios', 'Wellness & recovery', 'Multi-location operators'],
    challenges: [
      'Attracting and keeping members',
      'Manual membership billing',
      'Class schedules and reminders to manage',
      'Quiet sign-up periods',
      'Member data scattered across tools',
    ],
    stack: {
      it: ['Staff accounts and devices', 'Business Wi-Fi and cloud tools', 'Access controls', 'Backup configuration', 'Staff onboarding and offboarding'],
      marketing: ['Paid social campaigns', 'Landing pages', 'Social media content', 'Local visibility', 'Campaign reporting'],
      automation: ['Trial booking follow-up', 'CRM setup', 'Membership reminders', 'Re-engagement campaigns', 'Lead routing'],
      finance: ['Billing administration', 'Payment follow-up', 'Payroll administration', 'Cash-flow monitoring'],
    },
    workflow: ['Trial enquiry', 'Booking follow-up', 'CRM record', 'Membership reminder', 'Re-engagement', 'Billing administration', 'Payment follow-up'],
    outcomes: ['More trial enquiries followed up and converted', 'Membership billing administered reliably', 'Reminders and re-engagement running automatically', 'Systems that free you to focus on members'],
    included: ['Paid social plus trial and membership follow-up workflows', ...BASE_INCLUDED],
    notIncluded: ['Fitness or medical advice', 'Body-transformation guarantees', 'Automated medical screening', 'Misleading before-and-after claims', 'Sensitive health information in unsuitable systems'],
    related: ['healthcare', 'hospitality', 'retail'],
    faqs: [
      ['Can you help grow our membership?', 'Local social and paid campaigns are built to bring in trials and convert them to members. We do not make body-transformation or outcome guarantees.'],
      ['Can you manage recurring membership billing?', 'Yes. We administer recurring receivables and reconciliation so billing stays on track.'],
      ['Do you send class reminders automatically?', 'CRM automations handle reminders and follow-ups so fewer sessions are missed.'],
    ],
    seoTitle: 'Marketing, Billing & Systems for Fitness & Wellness | GrowthStack IT',
    seoDesc: 'Managed marketing, membership billing, IT and systems for Australian gyms, studios, and wellness businesses. More members, reliable billing. Book a Growth Call.',
  },
];

const SectorIcon = ({ k, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{SECTOR_ICONS[k]}</svg>
);

// ─────────────────────────────────────────────────────────────
// SECTOR SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────

// Small capability indicators shown on the priority sector cards.
const CapabilityChips = () => (
  <div className="cap-chips">
    {CAPABILITY_LABELS.map(c => <span key={c} className="cap-chip">{c}</span>)}
  </div>
);

// A horizontal step flow (business journey / example workflow). Wraps on small screens.
const FlowStrip = ({ steps, className = '' }) => (
  <div className={`flow-strip ${className}`}>
    {steps.map((step, i) => (
      <React.Fragment key={i}>
        <span className="flow-step">{step}</span>
        {i < steps.length - 1 && <span className="flow-arrow" aria-hidden="true">→</span>}
      </React.Fragment>
    ))}
  </div>
);

// Split into rows of at most `max`, balanced so we never leave a row with a
// single orphan. 9 steps becomes 3/3/3 rather than 4/4/1.
const balancedRows = (items, max = 4) => {
  const rowCount = Math.ceil(items.length / max);
  const per = Math.ceil(items.length / rowCount);
  const rows = [];
  for (let i = 0; i < items.length; i += per) rows.push(items.slice(i, i + per));
  return rows;
};

// Serpentine numbered track. The rail runs left to right along each row, drops
// at the end of the row, sweeps back and picks up the next row, then loops to
// step 1 at the end, because these workflows are cycles rather than dead ends.
const WorkflowTrack = ({ steps }) => {
  const rows = balancedRows(steps, 4);
  const cols = rows[0].length;
  const freeCell = cols - rows[rows.length - 1].length > 0;
  const loop = (
    <span className="wf-loop">
      <span className="wf-loop-ico" aria-hidden="true">↻</span>
      <span className="wf-loop-text">Back to step 1</span>
    </span>
  );

  let n = 0;
  return (
    <div className="wf-track" style={{ '--wf-cols': cols }}>
      {rows.map((row, r) => {
        const start = n;
        n += row.length;
        return (
          <React.Fragment key={r}>
            <ol className="wf-row" start={start + 1}>
              {row.map((step, i) => (
                <li className="wf-step" key={step}>
                  <span className="wf-num">{start + i + 1}</span>
                  <span className="wf-label">{step}</span>
                  {i < row.length - 1 && <span className="wf-link" aria-hidden="true" />}
                </li>
              ))}
              {r === rows.length - 1 && freeCell && <li className="wf-cell">{loop}</li>}
            </ol>
            {r < rows.length - 1 && (
              <div className="wf-return" aria-hidden="true"><span className="wf-tip" /></div>
            )}
          </React.Fragment>
        );
      })}
      {!freeCell && (
        <div className="wf-loop-bar"><span className="wf-tip" aria-hidden="true" />{loop}</div>
      )}
    </div>
  );
};

// Four equal service-stack cards for a sector. IT carries the same weight as the others.
const ServiceStack = ({ sector }) => (
  <div className="stack-grid">
    {STACK_PILLARS.map(p => (
      <div key={p.key} className="stack-card">
        <div className="stack-card-head">
          <span className="stack-ico">{ICONS[p.icon]}</span>
          <h3 className="stack-title">{p.label}</h3>
        </div>
        <ul className="stack-list">
          {sector.stack[p.key].map((item, j) => <li key={j}><CheckCircle />{item}</li>)}
        </ul>
      </div>
    ))}
  </div>
);

// "How we work" band, used in place of fabricated proof / case studies.
const WORKING_APPROACH = [
  ['01', 'Assess', 'On your Growth Call we map your current systems, enquiries, admin and finances, and agree where the pressure really is.'],
  ['02', 'Connect', 'We set up and connect only the services that fit your sector, so IT, marketing, automation and finance admin work together.'],
  ['03', 'Run', 'One accountable team runs the agreed services day to day and reports monthly on activity and outcomes.'],
];

const WorkingApproach = () => (
  <div className="steps-grid">
    {WORKING_APPROACH.map(([n, t, body]) => (
      <div key={n} className="step-card">
        <div className="step-num">{n}</div>
        <h3>{t}</h3>
        <p>{body}</p>
      </div>
    ))}
  </div>
);

const DisclaimerNote = ({ children }) => (
  <p className="disclaimer-note"><strong>Please note:</strong> {children}</p>
);

// ─────────────────────────────────────────────────────────────
// INDUSTRIES PAGE (connected model)
// ─────────────────────────────────────────────────────────────

const SectorsPage = ({ navigate }) => {
  const priority = SECTORS.filter(s => s.priority);
  const additional = SECTORS.filter(s => !s.priority);
  return (
    <>
      {/* 1 · Hero */}
      <div className="page-hero">
        <div className="container">
          <div className="eyebrow">◆ Industries we support</div>
          <h1>Technology and operations built around your industry.</h1>
          <p>GrowthStack IT helps trades, service businesses and growing SMEs maintain reliable IT systems, attract customers, automate administration and improve financial visibility through one connected partner.</p>
          <div className="hero-btn-row">
            <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>{CTA_LABEL}</button>
            <button className="btn btn-secondary btn-lg" onClick={() => navigate('services')}>Explore our services</button>
          </div>
        </div>
      </div>

      {/* 2 · Priority sectors */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Priority sectors</div>
            <h2>Built for businesses where technology, customers and operations must work together.</h2>
          </div>
          <div className="priority-grid">
            {priority.map(s => (
              <button key={s.key} className="priority-card" onClick={() => navigate('sector-' + s.key)}>
                <div className="pc-head">
                  <span className="pc-ico"><SectorIcon k={s.key} size={26} /></span>
                  <div>
                    <div className="pc-tag">{s.tag}</div>
                    <h3 className="pc-name">{s.name}</h3>
                  </div>
                </div>
                <div className="pc-row"><span className="pc-lbl">Challenge</span><p>{s.problem}</p></div>
                <div className="pc-row"><span className="pc-lbl">Outcome</span><p>{s.cardOutcome}</p></div>
                <CapabilityChips />
                <span className="pc-link">Explore solutions →</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 3 · Additional sectors */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ More sectors</div>
            <h2>We also support these industries.</h2>
          </div>
          <div className="sector-grid">
            {additional.map(s => (
              <button key={s.key} className="sector-card" onClick={() => navigate('sector-' + s.key)}>
                <span className="sector-card-ico"><SectorIcon k={s.key} size={24} /></span>
                <div className="sector-card-tag">{s.tag}</div>
                <h3 className="sector-card-name">{s.name}</h3>
                <p className="sector-card-outcome">{s.blurb}</p>
                <span className="sector-card-link">Explore {s.name} →</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · Common business problems */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Common pressure</div>
            <h2>Different sectors. Similar operational pressure.</h2>
          </div>
          <div className="problem-grid">
            {COMMON_PROBLEMS.map((p, i) => (
              <div key={i} className="problem-card">
                <span className="problem-num">{String(i + 1).padStart(2, '0')}</span>
                <p>{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 · The connected GrowthStack model */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ The connected model</div>
            <h2>Four capabilities. One connected operating partner.</h2>
          </div>
          <div className="connected-grid">
            {CONNECTED_MODEL.map(([title, desc], i) => (
              <div key={i} className="connected-card">
                <span className="cc-num">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="cc-title">{title}</h3>
                <p className="cc-desc">{desc}</p>
              </div>
            ))}
          </div>
          <DisclaimerNote>{FINANCE_DISCLAIMER}</DisclaimerNote>
        </div>
      </section>

      {/* 6 · Business journey */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ The business journey</div>
            <h2>From secure systems to revenue in the bank.</h2>
          </div>
          <div className="journey">
            <FlowStrip steps={BUSINESS_JOURNEY} className="journey-flow" />
            <div className="journey-foundation">
              <span className="jf-ico">{ICONS.IT}</span>
              <span>Secure IT foundation, supporting every stage above.</span>
            </div>
          </div>
        </div>
      </section>

      {/* 7 · Suitability */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Suitability</div>
            <h2>Is GrowthStack IT the right fit?</h2>
          </div>
          <div className="fit-grid">
            <div className="fit-col good">
              <h3>Good fit</h3>
              <ul>{SUITABILITY.good.map((t, i) => <li key={i}><CheckCircle />{t}</li>)}</ul>
            </div>
            <div className="fit-col bad">
              <h3>May not be the right fit</h3>
              <ul>{SUITABILITY.bad.map((t, i) => <li key={i}><span className="x-mark" aria-hidden="true">×</span>{t}</li>)}</ul>
            </div>
          </div>
        </div>
      </section>

      {/* 8 · How we work (in place of proof) */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ How we work</div>
            <h2>A connected partner, not four separate vendors.</h2>
            <p>We're building our public case studies. Until they're published, here's exactly how an engagement runs, with no invented numbers.</p>
          </div>
          <WorkingApproach />
        </div>
      </section>

      {/* 9 · Final CTA */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-banner-inner">
            <h2>Where is your business getting stuck?</h2>
            <p>Tell us whether the pressure is coming from unreliable systems, inconsistent enquiries, repetitive administration or financial processes. We'll help identify the right combination of services.</p>
            <div className="cta-banner-btns">
              <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>{CTA_LABEL}</button>
              <button className="btn btn-ghost btn-lg" onClick={() => navigate('services')}>Explore our services</button>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter navigate={navigate} />
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// SECTOR DETAIL PAGE (13-section structure, own URL for SEO)
// ─────────────────────────────────────────────────────────────

const SectorDetailPage = ({ sectorKey, navigate }) => {
  const s = SECTORS.find(x => x.key === sectorKey);
  if (!s) return null;
  const lower = s.name.toLowerCase();
  const related = (s.related || []).map(k => SECTORS.find(x => x.key === k)).filter(Boolean).slice(0, 3);
  return (
    <>
      {/* 1 · Breadcrumb + 2 · Hero */}
      <div className="service-hero sector-hero">
        <div className="container">
          <div className="breadcrumb">
            <button onClick={() => navigate('home')}>Home</button>
            <span className="sep">/</span>
            <button onClick={() => navigate('sectors')}>Industries</button>
            <span className="sep">/</span>
            <span className="current">{s.name}</span>
          </div>
          <div className="sector-hero-head">
            <span className="sector-badge lg"><SectorIcon k={s.key} size={30} /></span>
            <div>
              <div className="eyebrow" style={{ marginBottom: 6 }}>◆ {s.tag}</div>
              <h1>{s.h1}</h1>
            </div>
          </div>
          <p className="sector-hero-desc">{s.heroDesc}</p>
          <div className="sector-hero-ctas">
            <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>{CTA_LABEL}</button>
            <button className="btn btn-secondary btn-lg" onClick={() => navigate('pricing')}>View pricing</button>
          </div>
        </div>
      </div>

      {/* 3 · Who this is for */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Who this is for</div>
            <h2>Built for {lower} teams like these.</h2>
          </div>
          <div className="whofor-chips">
            {s.whoFor.map((w, i) => <span key={i} className="whofor-chip">{w}</span>)}
          </div>
        </div>
      </section>

      {/* 4 · Sector challenges */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ The challenges</div>
            <h2>Where {lower} businesses lose time.</h2>
          </div>
          <ul className="sector-list pain challenge-list">
            {s.challenges.map((c, j) => <li key={j}>{c}</li>)}
          </ul>
        </div>
      </section>

      {/* 5 · Recommended service combination */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Recommended combination</div>
            <h2>The connected stack for {lower}.</h2>
            <p>Four capabilities with equal weight, tuned to what this sector actually needs.</p>
          </div>
          <ServiceStack sector={s} />
          <DisclaimerNote>{FINANCE_DISCLAIMER}</DisclaimerNote>
        </div>
      </section>

      {/* 6 · Example workflow */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Example workflow</div>
            <h2>How the work moves.</h2>
          </div>
          <WorkflowTrack steps={s.workflow} />
        </div>
      </section>

      {/* 7 · Expected outcomes */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Expected outcomes</div>
            <h2>What good looks like.</h2>
          </div>
          <ul className="sector-list help outcomes-list">
            {s.outcomes.map((o, j) => <li key={j}><CheckCircle />{o}</li>)}
          </ul>
        </div>
      </section>

      {/* 8 + 9 · What's included / not included */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Scope</div>
            <h2>What's included, and what isn't.</h2>
          </div>
          <div className="incl-grid">
            <div className="incl-col yes">
              <h3>What's included</h3>
              <ul>{s.included.map((t, i) => <li key={i}><CheckCircle />{t}</li>)}</ul>
            </div>
            <div className="incl-col no">
              <h3>What's not included</h3>
              <ul>{s.notIncluded.map((t, i) => <li key={i}><span className="x-mark" aria-hidden="true">×</span>{t}</li>)}</ul>
            </div>
          </div>
          {s.disclaimer && <DisclaimerNote>{s.disclaimer}</DisclaimerNote>}
        </div>
      </section>

      {/* 10 · Working approach (in place of a case study) */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ How we work</div>
            <h2>Our approach with {lower}.</h2>
            <p>No invented case studies. Here's how a real engagement runs.</p>
          </div>
          <WorkingApproach />
        </div>
      </section>

      {/* 11 · FAQs */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ FAQ</div>
            <h2>{s.name}, answered.</h2>
          </div>
          <FAQ items={s.faqs} />
        </div>
      </section>

      {/* 12 · Related sectors */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Related sectors</div>
            <h2>Explore related industries.</h2>
          </div>
          <div className="sector-grid compact">
            {related.map(o => (
              <button key={o.key} className="sector-card" onClick={() => navigate('sector-' + o.key)}>
                <span className="sector-card-ico"><SectorIcon k={o.key} size={24} /></span>
                <h3 className="sector-card-name">{o.name}</h3>
                <span className="sector-card-link">Explore →</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 13 · Final CTA */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-banner-inner">
            <h2>Ready to connect it all for your {lower} business?</h2>
            <p>Book a Growth Call and we'll map where technology, marketing, automation and finance admin will make the biggest difference.</p>
            <div className="cta-banner-btns">
              <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>{CTA_LABEL}</button>
              <button className="btn btn-ghost btn-lg" onClick={() => navigate('sectors')}>All industries</button>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter navigate={navigate} />
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ABOUT PAGE
// ─────────────────────────────────────────────────────────────

const AboutPage = ({ navigate }) => (
  <>
    <div className="page-hero">
      <div className="container">
        <div className="eyebrow">◆ The Growth Gap</div>
        <h1>The story.</h1>
        <p>We've sat in your seat, running fragmented systems, chasing vendors, fixing payroll on Sunday night. GrowthStack IT is the firm we wished existed.</p>
      </div>
    </div>

    {/* Story Chapters */}
    <section className="section">
      <div className="container">
        <div className="story-chapter dir-left">
          <div>
            <div className="story-ch-num">CH. 01</div>
            <h2>The problem.</h2>
            <p>
              Businesses scale faster than their systems. Eleven SaaS tools, three accountants, one overworked office manager, and nothing talks to anything. The growth gap isn't a strategy problem. It's an operations problem.
            </p>
          </div>
          <picture>
            <source srcSet="assets/about-problem.webp" type="image/webp" />
            <img src="assets/about-problem.jpg" alt="The problem" className="story-img" width={1100} height={614} loading="lazy" decoding="async" />
          </picture>
        </div>

        <div className="story-chapter dir-right">
          <picture>
            <source srcSet="assets/about-insight.webp" type="image/webp" />
            <img src="assets/about-insight.jpg" alt="The insight" className="story-img" width={1100} height={614} loading="lazy" decoding="async" />
          </picture>
          <div>
            <div className="story-ch-num">CH. 02</div>
            <h2>The insight.</h2>
            <p>
              IT, marketing, finance, and automation aren't separate problems. They're one operations function, badly split across vendors. When they're managed as one, the whole business moves faster, and the owner can finally step back.
            </p>
          </div>
        </div>

        <div className="story-chapter dir-left">
          <div>
            <div className="story-ch-num">CH. 03</div>
            <h2>The fix.</h2>
            <p>
              A single managed operations pod. One stack, one bill, one accountable team. The growth gap, closed. This is what GrowthStack IT does, not as a consultancy, not as a vendor, but as your outsourced operations HQ.
            </p>
          </div>
          <picture>
            <source srcSet="assets/about-fix.webp" type="image/webp" />
            <img src="assets/about-fix.jpg" alt="The fix" className="story-img" width={1100} height={614} loading="lazy" decoding="async" />
          </picture>
        </div>
      </div>
    </section>

    {/* Our Approach (from Option C), before the Operators Behind section */}
    <section className="section section-navy approach-section">
      <div className="container">
        <div className="approach-grid">
          <div>
            <div className="eyebrow">◆ Our Approach</div>
            <h2>Operators first.<br />Vendors second.</h2>
          </div>
          <div className="approach-items">
            {[
              ['01', 'We run the stack, we don\'t pitch it.',
               'No reseller markups. No hidden affiliate fees. We make money when you stay, not when we up-sell.'],
              ['02', 'One pod, not a call centre.',
               'Same faces, same names, same accountability. You\'ll know your team within a week of onboarding.'],
              ['03', 'Show the numbers monthly.',
               'Uptime, leads, hours saved, reported on the first. If we\'re not improving your numbers, that\'s on us.'],
            ].map(([num, title, body]) => (
              <div key={num} className="approach-item">
                <span className="approach-num">{num}</span>
                <div>
                  <h4>{title}</h4>
                  <p>{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Numbers strip */}
    <section className="section section-mist">
      <div className="container">
        <div className="numbers-strip">
          {[
            ['100%', 'accent', 'Australian-owned'],
            ['11 → 1', null, 'Vendor consolidation, one Canberra client'],
            ['$2k+', 'accent', 'Saved per client / mo'],
          ].map(([n, accent, lbl]) => (
            <div key={lbl} className="n-stat">
              <div className={`n${accent ? ' accent' : ''}`}>{n}</div>
              <div className="lbl">{lbl}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* The Operators Behind */}
    <section className="section">
      <div className="container">
        <div className="section-head">
          <div className="eyebrow">◆ The Partners</div>
          <h2>The teams behind the stack.</h2>
        </div>
        <div className="partners-grid">
          {[
            {
              name: 'Tech Team',
              role: 'IT, Cloud & Automation',
              icon: 'IT',
              bio: 'The people who keep your infrastructure running: Google Workspace and Microsoft 365 setup, cloud infrastructure, device management, and backup. Certified across Google Cloud, Microsoft Azure, and modern MDM.',
            },
            {
              name: 'Finance Team',
              role: 'Finance Ops & Payroll Admin',
              icon: 'Fi',
              bio: 'The team behind your numbers, payroll, AP/AR, cashflow dashboards, and compliance. Xero Gold Partners who speak plain English and close the books on time, every month.',
            },
          ].map((p, i) => (
            <div key={i} className="partner-card">
              <div className="partner-icon">{ICONS[p.icon]}</div>
              <div>
                <div className="partner-name">{p.name}</div>
                <div className="partner-role">{p.role}</div>
                <div className="partner-bio">{p.bio}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <SiteFooter navigate={navigate} />
  </>
);

// ─────────────────────────────────────────────────────────────
// GROWTH CALL SLOT PICKER
// Availability comes from the Apps Script backend, which reads the real
// Google Calendar. See google-apps-script.js.
// ─────────────────────────────────────────────────────────────

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad2 = n => (n < 10 ? '0' + n : String(n));
const toYmd = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromYmd = s => { const p = String(s).split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); };
const monthIndex = d => d.getFullYear() * 12 + d.getMonth();

// Monday-first column for a JS day number.
const dowCol = d => (d.getDay() + 6) % 7;

const localTimeNote = (iso, tzLabel) => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const when = new Date(iso);
    const mine = when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();
    const theirs = when.toLocaleTimeString('en-AU', {
      hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Sydney',
    }).toLowerCase();
    if (mine === theirs) return '';
    return `That is ${mine} where you are (${zone}). Shown times are ${tzLabel}.`;
  } catch (err) {
    return '';
  }
};

const SlotPicker = ({ avail, state, day, slot, onPickDay, onPickSlot, onRetry }) => {
  const minDay = avail ? fromYmd(avail.minDate) : new Date();
  const maxDay = avail ? fromYmd(avail.maxDate) : new Date();
  const [cursor, setCursor] = useState(() => new Date(minDay.getFullYear(), minDay.getMonth(), 1));

  // Snap the visible month onto the window once availability arrives.
  useEffect(() => {
    if (avail) setCursor(new Date(minDay.getFullYear(), minDay.getMonth(), 1));
  }, [avail && avail.minDate]);

  if (state === 'loading') {
    return (
      <div className="slot-picker is-loading">
        <div className="slot-skeleton" />
        <p className="slot-hint">Checking live availability…</p>
      </div>
    );
  }

  if (state === 'error' || !avail) {
    return (
      <div className="slot-fallback">
        <p>
          We could not load live availability just now. Send the form through anyway and
          we will call you within one business day to lock in a time.
        </p>
        <button type="button" className="slot-retry" onClick={onRetry}>Try again</button>
      </div>
    );
  }

  const canPrev = monthIndex(cursor) > monthIndex(minDay);
  const canNext = monthIndex(cursor) < monthIndex(maxDay);
  const shift = n => setCursor(c => new Date(c.getFullYear(), c.getMonth() + n, 1));

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const total = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < dowCol(first); i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

  const times = day && avail.days[day] ? avail.days[day] : [];
  const chosen = times.find(t => t.time === slot);
  const note = chosen ? localTimeNote(chosen.iso, avail.tzLabel) : '';
  const openDays = Object.keys(avail.days).length;

  return (
    <div className="slot-picker">
      <div className="slot-cal">
        <div className="slot-cal-head">
          <button type="button" className="slot-nav" onClick={() => shift(-1)} disabled={!canPrev} aria-label="Previous month">‹</button>
          <div className="slot-month">{MONTH_LABELS[cursor.getMonth()]} {cursor.getFullYear()}</div>
          <button type="button" className="slot-nav" onClick={() => shift(1)} disabled={!canNext} aria-label="Next month">›</button>
        </div>
        <div className="slot-dow">
          {DOW_LABELS.map(l => <span key={l}>{l}</span>)}
        </div>
        <div className="slot-grid">
          {cells.map((d, i) => {
            if (!d) return <span key={`b${i}`} className="slot-day is-blank" />;
            const key = toYmd(d);
            const open = !!avail.days[key];
            return (
              <button
                key={key}
                type="button"
                className={`slot-day${open ? ' is-open' : ''}${key === day ? ' is-selected' : ''}`}
                disabled={!open}
                onClick={() => onPickDay(key)}
                aria-label={`${d.getDate()} ${MONTH_LABELS[d.getMonth()]}${open ? '' : ', unavailable'}`}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
        <p className="slot-hint">
          {openDays > 0
            ? `${openDays} day${openDays === 1 ? '' : 's'} open in the next month. Times shown in ${avail.tzLabel}.`
            : 'No open days in this window. Send the form and we will find you a time.'}
        </p>
      </div>

      <div className="slot-times">
        {!day ? (
          <p className="slot-empty">Pick a date to see available times.</p>
        ) : (
          <>
            <div className="slot-times-head">
              {fromYmd(day).getDate()} {MONTH_LABELS[fromYmd(day).getMonth()]}
              <span> · {avail.slotMinutes} min</span>
            </div>
            <div className="slot-chips">
              {times.map(t => (
                <button
                  key={t.time}
                  type="button"
                  className={`slot-chip${t.time === slot ? ' is-selected' : ''}`}
                  onClick={() => onPickSlot(t.time)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {note && <p className="slot-local">{note}</p>}
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// CONTACT PAGE
// ─────────────────────────────────────────────────────────────

const ContactPage = ({ navigate }) => {
  const [form, setForm] = useState({ fname:'', lname:'', company:'', email:'', phone:'', service:'', spend:'', message:'', website:'' });
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [avail, setAvail] = useState(null);
  const [availState, setAvailState] = useState('loading');
  const [day, setDay] = useState('');
  const [slot, setSlot] = useState('');

  const loadAvailability = useCallback(async () => {
    if (typeof fetch !== 'function') return; // prerender, stays on the skeleton
    setAvailState('loading');
    try {
      const res = await fetch(`${SHEET_WEBHOOK_URL}?action=availability`);
      const data = await res.json();
      if (!data || data.status !== 'ok' || !data.days) throw new Error('bad payload');
      setAvail(data);
      setAvailState('ready');
      const open = Object.keys(data.days).sort();
      setDay(prev => (prev && data.days[prev] ? prev : (open[0] || '')));
      setSlot(prev => {
        const target = open[0];
        return prev && target && data.days[target] && data.days[target].some(t => t.time === prev) ? prev : '';
      });
    } catch (err) {
      setAvail(null);
      setAvailState('error');
    }
  }, []);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const pickDay = key => { setDay(key); setSlot(''); setError(''); };
  const pickSlot = time => { setSlot(time); setError(''); };

  const booking = availState === 'ready' && !!avail;

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (booking && !slot) {
      setError('Please choose a date and time for your Growth Call.');
      return;
    }

    setSubmitting(true);
    const payload = {
      ...form,
      submitted_at: new Date().toISOString(),
      ...(booking ? { action: 'book', slot: `${day} ${slot}` } : {}),
    };

    try {
      const res = await fetch(SHEET_WEBHOOK_URL, {
        method: 'POST',
        // text/plain keeps this a simple request, so the browser skips the
        // CORS preflight that Apps Script cannot answer, and we can read the reply.
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.status === 'success') {
        setSubmitted(data.booking || true);
      } else if (data.code === 'taken') {
        setError(data.message || 'That time was just taken. Please pick another.');
        setSlot('');
        loadAvailability();
      } else {
        setError(data.message || 'Something went wrong.');
      }
    } catch (err) {
      setError('network');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-hero">
        <div className="container">
          <div className="eyebrow">◆ Contact</div>
          <h1>Let's close the growth gap.</h1>
          <p>Book a 60-minute Growth Call. Pick a time that suits you, and we'll map your stack and show you where we can help.</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="contact-layout">

            {/* Form */}
            <div className="contact-form-card">
              <h2>Book a Growth Call</h2>
              <p>We'll review your entire stack, every tool, every vendor, every gap, and hand you the report. It's a no-obligation Growth Call, with clear next steps if you decide to come on board.</p>

              {submitted ? (
                <div className="form-success">
                  {submitted.dayLabel ? (
                    <>
                      <p>✓ You're booked in.</p>
                      <div className="booked-when">
                        <strong>{submitted.dayLabel}</strong>
                        <span>{submitted.timeLabel} {submitted.tzLabel}</span>
                      </div>
                      {submitted.meetLink && (
                        <a className="btn btn-primary booked-link" href={submitted.meetLink} target="_blank" rel="noopener noreferrer">
                          Join with Google Meet →
                        </a>
                      )}
                      <p className="booked-note">
                        The calendar invite and a confirmation email are on their way to {form.email}.
                        Need to move it? Just reply to that email.
                      </p>
                    </>
                  ) : (
                    <p>✓ Thanks! We'll be in touch within one business day to confirm your session and get you booked in.</p>
                  )}
                </div>
              ) : (
                <form onSubmit={submit}>
                  <div className="form-row">
                    <div className="form-field">
                      <label>First name</label>
                      <input name="fname" type="text" placeholder="Alex" value={form.fname} onChange={handle} required />
                    </div>
                    <div className="form-field">
                      <label>Last name</label>
                      <input name="lname" type="text" placeholder="Chen" value={form.lname} onChange={handle} required />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Company name</label>
                    <input name="company" type="text" placeholder="Acme Pty Ltd" value={form.company} onChange={handle} required />
                  </div>
                  <div className="form-row">
                    <div className="form-field">
                      <label>Work email</label>
                      <input name="email" type="email" placeholder="alex@acme.com.au" value={form.email} onChange={handle} required />
                    </div>
                    <div className="form-field">
                      <label>Phone</label>
                      <input name="phone" type="tel" placeholder="+61 4XX XXX XXX" value={form.phone} onChange={handle} />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Which services interest you?</label>
                    <select name="service" value={form.service} onChange={handle} required>
                      <option value="">Select a service area…</option>
                      <option>IT Systems &amp; Security</option>
                      <option>Digital Marketing &amp; Web</option>
                      <option>Finance &amp; Payroll Admin</option>
                      <option>Operations &amp; Automation</option>
                      <option>Full Stack (all four pillars)</option>
                      <option>Not sure, just exploring</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Approximate monthly vendor spend (optional)</label>
                    <select name="spend" value={form.spend} onChange={handle}>
                      <option value="">Select a range…</option>
                      <option>Under $1,000 / month</option>
                      <option>$1,000 – $3,000 / month</option>
                      <option>$3,000 – $7,000 / month</option>
                      <option>$7,000 – $15,000 / month</option>
                      <option>Over $15,000 / month</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Tell us about your current situation</label>
                    <textarea
                      name="message" rows="4"
                      placeholder="What's your biggest operations headache right now? The more context, the more useful your Growth Call will be."
                      value={form.message} onChange={handle}
                    />
                  </div>

                  <div className="form-field slot-field">
                    <label>Pick your Growth Call time</label>
                    <SlotPicker
                      avail={avail}
                      state={availState}
                      day={day}
                      slot={slot}
                      onPickDay={pickDay}
                      onPickSlot={pickSlot}
                      onRetry={loadAvailability}
                    />
                  </div>

                  {/* Honeypot. Hidden from people, catches bots. */}
                  <input
                    name="website" type="text" tabIndex="-1" autoComplete="off"
                    value={form.website} onChange={handle}
                    aria-hidden="true" className="hp-field"
                  />

                  {error && (
                    <div className="form-error">
                      {error === 'network'
                        ? <>Something went wrong, please email us directly at <strong>sales@growthstackit.com.au</strong></>
                        : error}
                    </div>
                  )}
                  <div className="form-submit-wrap">
                    <button type="submit" className="btn btn-primary btn-lg btn-full" disabled={submitting}>
                      {submitting
                        ? (booking ? 'Booking…' : 'Sending…')
                        : (booking ? 'Confirm my Growth Call →' : 'Submit enquiry →')}
                    </button>
                  </div>
                  <p className="form-note">
                    {booking
                      ? 'You will get a calendar invite and a Google Meet link straight away. The Growth Call is a no-obligation review of your stack.'
                      : "We'll confirm your session within one business day. The Growth Call is a no-obligation review of your stack."}
                  </p>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="contact-sidebar">
              <div className="contact-info-card">
                <h3>Get in touch directly</h3>
                <div className="c-info-item">
                  <span className="c-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                  </span>
                  <div>
                    <div className="lbl">Office</div>
                    <div className="val">Canberra, Australia</div>
                  </div>
                </div>
                <div className="c-info-item">
                  <span className="c-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.82 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.77 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </span>
                  <div>
                    <div className="lbl">Phone</div>
                    <div className="val">0410 847 664<br /><span style={{opacity:.6, fontSize:12}}>Mon–Fri 9am–5pm AEST</span></div>
                  </div>
                </div>
                <div className="c-info-item">
                  <span className="c-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </span>
                  <div>
                    <div className="lbl">Email</div>
                    <div className="val">sales@growthstackit.com.au</div>
                  </div>
                </div>
                <div className="c-info-item">
                  <span className="c-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </span>
                  <div>
                    <div className="lbl">Response time</div>
                    <div className="val">We respond within 1 business hour.</div>
                  </div>
                </div>
              </div>

              <div className="next-steps-card">
                <h4>What happens next?</h4>
                {[
                  ['We review your enquiry and confirm within one business day.', 'Confirmation'],
                  ['A 60-minute Growth Call is scheduled at your convenience.', 'Growth Call'],
                  ['We hand you a report on your stack, every tool, every gap, yours to keep.', 'Your report'],
                  ['If it\'s a fit, we onboard you within 30 days.', 'Onboarding'],
                ].map(([body, title], i) => (
                  <div key={i} className="next-step">
                    <span className="n-dot">{i + 1}</span>
                    <div className="ns-text"><strong>{title}: </strong>{body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter navigate={navigate} />
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// SERVICE DETAIL DATA
// ─────────────────────────────────────────────────────────────

const SERVICE_DATA = {
  it: {
    key: 'it',
    icon: 'IT',
    eyebrow: 'IT Systems & Security',
    headline: 'Your IT, finally boring.',
    description: 'Google Workspace and Microsoft 365 setup, cloud infrastructure, and device management, delivered as a single managed service under a documented uptime commitment. We take full accountability for your stack so you never have to chase a vendor again.',
    kpis: [['Documented','Uptime commitment'],['AU','Data sovereignty'],['24/7','Monitoring']],
    features: [
      { title: 'Google Workspace & Microsoft 365', items: ['Complete mailbox, calendar, and file migration','MX, SPF, DKIM, and DMARC configuration','Departmental access and profile provisioning','Post-migration hypercare support'] },
      { title: 'Cloud Infrastructure',             items: ['Azure and cloud server management','Domain, DNS, and email configuration','Server patching, snapshots, and monitoring','Firewall rule logging and access control'] },
      { title: 'Device Management',                items: ['Mac and PC fleet management via MDM','Employee onboarding and offboarding kits','Software licensing and patch management','Mobile device management (iOS & Android)'] },
      { title: 'Backup & Disaster Recovery',       items: ['RTO and RPO target workshops','Immutable cloud backup architecture','Automated backup and state retention','Failover testing and verification'] },
    ],
    tools: ['Microsoft 365','Google Workspace','Azure','Intune','Jamf','Autopilot','Cloudflare','Veeam'],
    outcomes: [
      { n:'Documented', l:'Uptime commitment', d:'Written into every managed agreement and reported monthly.' },
      { n:'30 days',l:'Onboarding',       d:'From kickoff to a fully managed environment.' },
      { n:'24/7',   l:'Monitoring',       d:'Infrastructure alerts around the clock.' },
      { n:'AU',     l:'Data sovereignty', d:'All data stored in Australian data centres by default.' },
    ],
    process: [
      ['Assess',   'We review every device, account, licence, and configuration. Yours to keep.'],
      ['Migrate',  'We standardise and migrate your stack to the right tools, cleanly and with minimal downtime.'],
      ['Protect',  'Backup, disaster recovery, and access controls put in place across the environment.'],
      ['Operate',  'Ongoing monitoring, patch management, and updates, all included monthly.'],
    ],
    faqs: [
      ['Do you support both Mac and Windows?','Yes. We manage mixed Mac and PC environments, MDM, and software deployment, for teams of all sizes.'],
      ['What happens if something breaks after hours?','P1 incidents (full outages) are covered by on-call infrastructure support. P2 and below are handled the next business day with a same-day target.'],
      ['Can you replace our current IT provider?','Yes. We handle the full transition including documentation and credentials. Most clients are transitioned within 2 to 3 weeks.'],
      ['Do you support businesses outside Canberra?','Our managed services are Australia-wide. On-site visits are available in and around Canberra. Remote-only clients across AU are fully supported.'],
    ],
    related: ['marketing','finance','operations'],
  },
  marketing: {
    key: 'marketing',
    icon: 'Mk',
    eyebrow: 'Digital Marketing & Web',
    headline: 'Marketing that proves its worth.',
    description: 'Google and Meta Ads management, SEO, web design, and conversion optimisation, all measured, reported, and accountable to a number. Every dollar tracked back to a result.',
    kpis: [['3.2×','Avg. ROAS'],['28%','CPA reduction'],['14-day','Campaign launch']],
    features: [
      { title: 'Paid Advertising',    items: ['Google Search, Display, and Shopping campaigns','Meta (Facebook & Instagram) Ads management','Bid strategy and budget optimisation','Conversion tracking and attribution setup'] },
      { title: 'SEO & Content',       items: ['Technical SEO audit and on-page optimisation','Local SEO for Australian markets','Content strategy and copywriting','Google Business Profile management'] },
      { title: 'Web Design & CRO',    items: ['High-converting landing page design and build','Website maintenance and updates','A/B testing and conversion rate optimisation','Core Web Vitals and page speed optimisation'] },
      { title: 'Reporting & Analytics', items: ['Monthly performance reports with plain-language summaries','GA4 and Google Tag Manager setup','Custom dashboards for key business metrics','Quarterly strategy reviews with your account lead'] },
    ],
    tools: ['Google Ads','Meta Ads','Google Analytics 4','Search Console','Semrush','Hotjar','Webflow','Google Tag Manager'],
    outcomes: [
      { n:'3.2×',  l:'Average ROAS',    d:'Return on ad spend across managed Google and Meta campaigns.' },
      { n:'28%',   l:'CPA reduction',   d:'Average cost-per-acquisition improvement after 90 days.' },
      { n:'14-day',l:'Campaign launch', d:'From briefing to live campaigns in two weeks.' },
      { n:'Monthly',l:'Reporting',      d:'Plain-language results report, every month without chasing.' },
    ],
    process: [
      ['Assess',   'We review your current campaigns, website, SEO standing, and analytics setup. We find the leaks before we spend a dollar.'],
      ['Strategy', 'A 90-day growth plan with channel priorities, budget allocation, and quick wins clearly mapped.'],
      ['Launch',   'Campaigns, landing pages, and tracking go live. Everything is tested before a dollar is spent.'],
      ['Optimise', 'Weekly campaign optimisations and monthly reporting. Your account lead is reachable, not a faceless dashboard.'],
    ],
    faqs: [
      ['Do you lock me into a minimum ad spend?','No. We recommend a minimum budget for statistically meaningful data, but the budget is yours, we just manage it.'],
      ['Can you build or redesign our website?','Yes. Landing pages and full website builds are included depending on your tier. We build on Webflow for performance.'],
      ['How long before we see SEO results?','Typically 3–6 months for meaningful organic ranking improvements. We show progress on leading indicators from month one.'],
      ['Do you run ads for e-commerce?','Yes, including Google Shopping. We work with both service businesses and e-commerce clients across AU.'],
    ],
    related: ['it','finance','operations'],
  },
  finance: {
    key: 'finance',
    icon: 'Fi',
    eyebrow: 'Finance & Payroll Admin',
    headline: 'Pro finances without a full-time CFO.',
    description: 'Payroll processing, accounts payable and receivable, cashflow dashboards, and super compliance, handled on time, every time. We use Xero and MYOB so your data always stays yours.',
    kpis: [['14-day','Monthly close'],['100%','STP compliance'],['Zero','Payroll errors']],
    features: [
      { title: 'Payroll Processing',      items: ['Weekly, fortnightly, or monthly payroll runs','Single Touch Payroll (STP) lodgement','Leave management and accruals','Termination and redundancy calculations'] },
      { title: 'Accounts Payable & Receivable', items: ['Invoice processing and supplier payments','Customer invoicing and collections follow-up','Creditor and debtor reconciliation','Purchase order matching and approval workflows'] },
      { title: 'Cashflow & Reporting',    items: ['Monthly cashflow dashboard (Xero or MYOB)','Accounts reconciliation by the 14th of each month','Budget vs. actuals reporting','Custom financial reporting on request'] },
      { title: 'Super & Compliance',      items: ['Superannuation guarantee calculations and lodgements','SGC penalty monitoring and compliance alerts','PAYG withholding summaries','Liaison with your external accountant or CPA'] },
    ],
    tools: ['Xero','MYOB','Dext','Hubdoc','Employment Hero','KeyPay','ATO Business Portal','Stripe'],
    outcomes: [
      { n:'14-day', l:'Monthly close',     d:'Reconciliation and dashboard delivered by the 14th, every month.' },
      { n:'100%',   l:'STP compliance',    d:'Zero late lodgements since inception.' },
      { n:'Zero',   l:'Payroll errors',    d:'Dual-check process on every single payroll run.' },
      { n:'1 day',  l:'Invoice turnaround',d:'Supplier invoices processed within one business day.' },
    ],
    process: [
      ['Assess',   'We review your payroll setup, chart of accounts, open invoices, and current compliance position.'],
      ['Set up',   'We configure Xero or MYOB to your business structure, import historical data, and connect bank feeds.'],
      ['Operate',  'Payroll runs, invoice processing, and reconciliation handled on your schedule, every single month.'],
      ['Report',   'Cashflow dashboard and monthly summary delivered by the 14th, with plain-English commentary.'],
    ],
    faqs: [
      ['Are you a registered tax agent or CPA?','No. We provide administrative finance support only, payroll, AP/AR, and bookkeeping. Tax advisory and BAS lodgements are referred to our CPA partners.'],
      ['Do you work with Xero or MYOB?','Both. We are Xero Silver Partners and MYOB Certified Advisors. We recommend Xero for most clients but work with whatever you already use.'],
      ['Can you handle payroll for casual and part-time staff?','Yes. We manage complex award-rate calculations, overtime, and mixed employment types using Employment Hero or KeyPay.'],
      ['What if I already have a bookkeeper?','We can work alongside your bookkeeper or transition the work over cleanly. We will brief them fully and ensure nothing falls through the gap.'],
    ],
    related: ['it','marketing','operations'],
  },
  operations: {
    key: 'operations',
    icon: 'Op',
    eyebrow: 'Operations & Automation',
    headline: 'A business that runs on systems.',
    description: 'Workflow design, Zapier and Make integrations, GoHighLevel CRM setup, and SOP documentation, we build the operating system that lets your business run without you in every room.',
    kpis: [['8+ hrs','Saved per week'],['30 days','First automation live'],['3×','Faster onboarding']],
    features: [
      { title: 'Workflow Design',         items: ['End-to-end process mapping and documentation','Bottleneck identification and elimination','Approval workflow design and implementation','Cross-department handoff optimisation'] },
      { title: 'Automation & Integrations', items: ['Zapier and Make (Integromat) workflow builds','CRM, invoicing, and comms tool integrations','Trigger-based automations across your stack','Full automation audit, finding what can be automated'] },
      { title: 'CRM & Pipeline',          items: ['GoHighLevel CRM setup and customisation','Sales pipeline design and lead management','Automated follow-up sequences','Team training and onboarding to new systems'] },
      { title: 'SOPs & Documentation',    items: ['Standard operating procedure creation','Process documentation in Notion or your preferred tool','Staff onboarding playbooks','Quarterly SOP review and updates'] },
    ],
    tools: ['Zapier','Make (Integromat)','GoHighLevel','Notion','Monday.com','Slack','HubSpot','ActiveCampaign'],
    outcomes: [
      { n:'8+ hrs',  l:'Saved per week',        d:'Average time reclaimed after automation implementation.' },
      { n:'100%',    l:'Run on systems',         d:'Every client leaves with documented, repeatable processes.' },
      { n:'30 days', l:'First automation live',  d:'From briefing to your first automated workflow running.' },
      { n:'3×',      l:'Faster onboarding',      d:'Average improvement in new staff time-to-productivity.' },
    ],
    process: [
      ['Map',      'We document every key workflow in your business, what happens, who does it, what tools are involved, and where it breaks.'],
      ['Design',   'We redesign for efficiency and automation-readiness, then get your sign-off before building anything.'],
      ['Build',    'Automations, CRM, and integrations are built and tested. Staff are trained before go-live.'],
      ['Document', 'Every workflow is documented as a SOP. Your business does not depend on any one person\'s memory, including ours.'],
    ],
    faqs: [
      ['Do I need to know how Zapier or Make works?','No. We design, build, and maintain all automations. We will show you how to monitor them, but you do not need to touch the underlying logic.'],
      ['We already have a CRM, can you work with it?','Yes. We work with HubSpot, Salesforce, Pipedrive, and others. We will only recommend a migration to GoHighLevel if it genuinely fits better.'],
      ['What if an automation breaks?','All automations built by our team are monitored and included in our support SLA. If something breaks, we fix it at no extra charge.'],
      ['How do you document our SOPs?','We use Notion by default, it is free and powerful. We can also document in Confluence, Google Docs, Microsoft OneNote, or any tool your team already uses.'],
    ],
    related: ['it','marketing','finance'],
  },
};

// ─────────────────────────────────────────────────────────────
// SERVICE DETAIL PAGE
// ─────────────────────────────────────────────────────────────

const ServiceDetailPage = ({ serviceKey, navigate }) => {
  const s = SERVICE_DATA[serviceKey];
  if (!s) return null;

  return (
    <>
      {/* Hero */}
      <div className="service-hero">
        <div className="container">
          <div className="breadcrumb">
            <button onClick={() => navigate('home')}>Home</button>
            <span className="sep">/</span>
            <button onClick={() => navigate('services')}>Services</button>
            <span className="sep">/</span>
            <span className="current">{s.eyebrow}</span>
          </div>
          <div className="service-hero-inner">
            <div>
              <div className="service-hero-icon">{ICONS[s.icon]}</div>
              <div className="eyebrow">{s.eyebrow}</div>
              <h1>{s.headline}</h1>
              <p>{s.description}</p>
              <div className="service-hero-ctas">
                <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>
                  Book a Growth Call →
                </button>
                <button className="btn btn-secondary btn-lg" onClick={() => navigate('pricing')}>
                  See pricing
                </button>
              </div>
            </div>
            <div className="service-hero-kpis">
              {s.kpis.map(([n, l]) => (
                <div key={l} className="hero-kpi">
                  <div className={`kpi-n${n.length > 7 ? ' word' : ''}`}>{n}</div>
                  <div className="kpi-lbl">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* What's included */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ What's Included</div>
            <h2>Everything in the {s.eyebrow} pillar.</h2>
          </div>
          <div className="feature-cats-grid">
            {s.features.map((cat, i) => (
              <div key={i} className="feature-cat">
                <h4>{cat.title}</h4>
                <ul>
                  {cat.items.map((item, j) => (
                    <li key={j}><CheckCircle />{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="tools-section section-mist">
        <div className="container">
          <div className="eyebrow" style={{ marginBottom: 8 }}>◆ Tools & Platforms</div>
          <h3>The stack we use to deliver it.</h3>
          <div className="tools-strip" style={{ marginTop: 20 }}>
            {s.tools.map(t => (
              <span key={t} className="tool-tag">
                <span className="tool-dot"></span>{t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ Outcomes</div>
            <h2>What you can expect.</h2>
          </div>
          <div className="service-outcomes-grid">
            {s.outcomes.map((o, i) => (
              <div key={i} className="svc-outcome">
                <div className="sn">{o.n}</div>
                <div className="sl">{o.l}</div>
                <div className="sd">{o.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ How We Deliver It</div>
            <h2>The process, step by step.</h2>
          </div>
          <div className="svc-process">
            {s.process.map(([title, body], i) => (
              <div key={i} className="svc-step">
                <div className="svc-step-num">{String(i + 1).padStart(2,'0')}</div>
                <div>
                  <h4>{title}</h4>
                  <p>{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ FAQ</div>
            <h2>Questions about {s.eyebrow}.</h2>
          </div>
          <FAQ items={s.faqs} />
        </div>
      </section>

      {/* Related services */}
      <section className="section section-mist">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">◆ The Full Stack</div>
            <h2>Explore the other pillars.</h2>
            <p>Each service is stronger when the others run alongside it.</p>
          </div>
          <div className="related-grid">
            {s.related.map(rk => {
              const r = SERVICE_DATA[rk];
              return (
                <div key={rk} className="related-card" onClick={() => navigate('service-' + rk)}>
                  <span className="rc-icon">{ICONS[r.icon]}</span>
                  <h4>{r.eyebrow}</h4>
                  <p>{r.description.slice(0, 90)}…</p>
                  <div className="rc-link">Explore →</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-banner-inner">
            <h2>Ready to get started with {s.eyebrow}?</h2>
            <p>Book a 60-minute Growth Call. We'll review your current setup and tell you exactly what needs to change.</p>
            <div className="cta-banner-btns">
              <button className="btn btn-primary btn-lg" onClick={() => navigate('contact')}>Book a Growth Call →</button>
              <button className="btn btn-ghost btn-lg" onClick={() => navigate('pricing')}>View pricing</button>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter navigate={navigate} />
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// APP ROOT
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// SEO HELPERS
// ─────────────────────────────────────────────────────────────

const SITE_URL = 'https://growthstackit.com.au';

const PAGE_DESCRIPTIONS = {
  home:                 'IT systems and security, digital marketing and web, operations and automation, finance and payroll admin. Four services, one partner, for Australian SMEs.',
  services:             'Managed IT, digital marketing, finance admin, and business automation for Australian SMEs. One team, full accountability. Book a Growth Call today.',
  sectors:              'Managed operations tuned for your industry: Tradies, Construction, Healthcare, Professional Services, Hospitality, Retail, Real Estate, and Fitness. Marketing, finance, automation, and cloud IT as one team for Australian SMEs.',
  pricing:              'GrowthStack IT pricing, bundled managed plans from $1,200/mo AUD plus an à la carte service catalogue across Growth Marketing, Business Systems & AI, Finance & Payroll, IT Systems & Security, and Business Transformation Projects for Australian SMEs.',
  about:                'GrowthStack IT is an Australian-owned managed operations firm based in Canberra. We consolidate your IT, marketing, finance and automation under one team and one monthly bill.',
  contact:              'Book a 60-minute Growth Call with GrowthStack IT. We map your current stack, find every gap, and hand you a report.',
  'service-it':         'Managed cloud infrastructure, Google Workspace and Microsoft 365 setup, and device management for Australian SMEs. Documented uptime commitment and AU data sovereignty. Canberra-based.',
  'service-marketing':  'Google Ads, Meta Ads, SEO, and web design for Australian businesses. Data-driven digital marketing with transparent monthly reporting. Average 3.2× ROAS.',
  'service-finance':    'Payroll processing, accounts payable/receivable, and Xero/MYOB bookkeeping for Australian SMEs. 100% STP compliance. Monthly close by the 14th, every month.',
  'service-operations': 'Business process automation, Zapier and Make integrations, GoHighLevel CRM setup, and SOP documentation for Australian SMEs. Save 8+ hours per week.',
  ...Object.fromEntries(SECTORS.map(s => ['sector-' + s.key, s.seoDesc])),
};

function setMetaDesc(content) {
  const el = document.querySelector('meta[name="description"]');
  if (el) el.setAttribute('content', content);
}

function setMetaAttr(selector, content) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute('content', content);
}

function setCanonical(path) {
  const el = document.querySelector('link[rel="canonical"]');
  if (el) el.setAttribute('href', SITE_URL + (path || '/'));
}

function injectSchema(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeSchema(id) {
  document.getElementById(id)?.remove();
}

const PAGE_TITLES = {
  home:                 'GrowthStack IT | IT & Security, Marketing, Operations & Finance Admin',
  services:             'Managed Operations Services | GrowthStack IT Australia',
  sectors:              'Industries We Serve | Industry-Specific Managed Operations | GrowthStack IT',
  pricing:              'Plans & Pricing | Marketing, Automation, Finance & IT | GrowthStack IT',
  about:                'About GrowthStack IT | Australian-Owned Managed Operations',
  contact:              'Book a Growth Call | GrowthStack IT',
  'service-it':         'IT Systems & Security Canberra | Managed IT & Cloud | GrowthStack IT',
  'service-marketing':  'Digital Marketing & SEO Services Australia | GrowthStack IT',
  'service-finance':    'Payroll & Finance Administration Canberra | GrowthStack IT',
  'service-operations': 'Business Automation & Operations Australia | GrowthStack IT',
  ...Object.fromEntries(SECTORS.map(s => ['sector-' + s.key, s.seoTitle])),
};

const PAGE_PATHS = {
  home:                 '/',
  services:             '/services',
  sectors:              '/sectors',
  pricing:              '/pricing',
  about:                '/about',
  contact:              '/contact',
  'service-it':         '/services/it',
  'service-marketing':  '/services/marketing',
  'service-finance':    '/services/finance',
  'service-operations': '/services/operations',
  ...Object.fromEntries(SECTORS.map(s => ['sector-' + s.key, '/sectors/' + s.key])),
};

const PATH_TO_PAGE = {
  '/':                    'home',
  '/services':            'services',
  '/sectors':             'sectors',
  '/pricing':             'pricing',
  '/about':               'about',
  '/contact':             'contact',
  '/services/it':         'service-it',
  '/services/marketing':  'service-marketing',
  '/services/finance':    'service-finance',
  '/services/operations': 'service-operations',
  ...Object.fromEntries(SECTORS.map(s => ['/sectors/' + s.key, 'sector-' + s.key])),
};

function pageFromPath() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return PATH_TO_PAGE[path] || 'home';
}

function App() {
  const [page, setPage] = useState(pageFromPath);
  const gaSent = useRef(false);

  const navigate = useCallback((p) => {
    setPage(p);
    history.pushState(null, '', PAGE_PATHS[p] || '/');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    const onPop = () => setPage(pageFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const title = PAGE_TITLES[page] || 'GrowthStack IT';
    const desc = PAGE_DESCRIPTIONS[page] || PAGE_DESCRIPTIONS.home;
    const url = SITE_URL + (PAGE_PATHS[page] || '/');
    document.title = title;
    setMetaDesc(desc);
    setCanonical(PAGE_PATHS[page] || '/');
    setMetaAttr('meta[property="og:title"]', title);
    setMetaAttr('meta[property="og:description"]', desc);
    setMetaAttr('meta[property="og:url"]', url);
    setMetaAttr('meta[name="twitter:title"]', title);
    setMetaAttr('meta[name="twitter:description"]', desc);

    // GA4 pageview for client-side navigation. The first view is already sent
    // by gtag('config') in the HTML shell, so only later route changes fire here.
    if (gaSent.current && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: title,
        page_path: PAGE_PATHS[page] || '/',
        page_location: url,
      });
    }
    gaSent.current = true;

    const faqData = page === 'services' ? SERVICES_FAQ
      : page === 'pricing' ? PRICING_FAQ
      : page.startsWith('service-') ? SERVICE_DATA[page.replace('service-', '')]?.faqs
      : page.startsWith('sector-') ? SECTORS.find(x => x.key === page.replace('sector-', ''))?.faqs
      : null;

    if (faqData) {
      injectSchema('faq-schema', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData.map(([q, a]) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      });
    } else {
      removeSchema('faq-schema');
    }

    if (page.startsWith('service-')) {
      const sKey = page.replace('service-', '');
      const s = SERVICE_DATA[sKey];
      if (s) {
        injectSchema('breadcrumb-schema', {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL + '/' },
            { '@type': 'ListItem', position: 2, name: 'Services', item: SITE_URL + '/services' },
            { '@type': 'ListItem', position: 3, name: s.eyebrow, item: SITE_URL + (PAGE_PATHS[page] || '/') },
          ],
        });
      }
    } else if (page.startsWith('sector-')) {
      const sec = SECTORS.find(x => x.key === page.replace('sector-', ''));
      if (sec) {
        injectSchema('breadcrumb-schema', {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL + '/' },
            { '@type': 'ListItem', position: 2, name: 'Industries', item: SITE_URL + '/sectors' },
            { '@type': 'ListItem', position: 3, name: sec.name, item: SITE_URL + (PAGE_PATHS[page] || '/') },
          ],
        });
      }
    } else {
      removeSchema('breadcrumb-schema');
    }
  }, [page]);

  const pages = {
    home:                 <HomePage                                  navigate={navigate} />,
    services:             <ServicesPage                              navigate={navigate} />,
    sectors:              <SectorsPage                               navigate={navigate} />,
    pricing:              <PricingPage                               navigate={navigate} />,
    about:                <AboutPage                                 navigate={navigate} />,
    contact:              <ContactPage                               navigate={navigate} />,
    'service-it':         <ServiceDetailPage serviceKey="it"         navigate={navigate} />,
    'service-marketing':  <ServiceDetailPage serviceKey="marketing"  navigate={navigate} />,
    'service-finance':    <ServiceDetailPage serviceKey="finance"    navigate={navigate} />,
    'service-operations': <ServiceDetailPage serviceKey="operations" navigate={navigate} />,
    ...Object.fromEntries(SECTORS.map(s => ['sector-' + s.key, <SectorDetailPage sectorKey={s.key} navigate={navigate} />])),
  };

  return (
    <div>
      <SiteNav activePage={page} navigate={navigate} />
      <main className="page-enter" key={page}>
        {pages[page]}
      </main>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
